import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
    Table, Tag, Button, Modal, Form, Input, DatePicker, InputNumber,
    Select, Spin, Empty, Descriptions, Divider, Badge, Tooltip, Space,
    Alert, Drawer, Typography, Row, Col, Steps, Radio, Card
} from 'antd';
import CBInputNumber from '../../../components/CBInputNumber';
import {
    PlusOutlined, StopOutlined, EyeOutlined, CheckCircleOutlined,
    ClockCircleOutlined, CloseCircleOutlined,
    UserOutlined, BankOutlined, ReloadOutlined,
    FileDoneOutlined, PrinterOutlined, FileTextOutlined, ArrowRightOutlined,
    SafetyOutlined,
    LinkOutlined, DisconnectOutlined, SearchOutlined,
    TeamOutlined, ExportOutlined, RightOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import axiosInstance from "../../../variables/api/axiosInstance";
import { formatDate, formatMontant, isDossierMurOuPlus, STATUT_MAP } from "../../../variables/constants";
import KpiCards from '../../../components/KpiCards';
import OrdreDeDepenseV2 from './OrdreDeDepense';
import { aplatirOperations, aplatirEnListe } from './arborescenceReglements';
import QuittanceIndemnite from './QuittanceIndemnite';
import {
    chargerSignatureImprimable, confirmerImpressionSansSignature,
} from '../../../components/avertissementSignature';
import { optionsPrejudice, optionsExpert, estCompatible } from '../../../utils/compatibiliteExpertPrejudice';
import { printInIframe } from '../../../utils/printIframe';
import { confirmerChampsManquants } from '../../../utils/confirmationImpression';
import quittanceCss from '../../../assets/styles/quittanceIndemniteRaw';
import '../../../assets/styles/reglement.css';
import logoLocal from '../../../assets/images/logo.png';
import { useQuery } from '@tanstack/react-query';
import TableExportButton from '../../../components/TableExportButton';
import ParamMotifSelect from '../../../components/ParamMotifSelect';
import EncaissementImputationPicker from '../../../components/EncaissementImputationPicker';
import { useTauxRetenue } from '../../../services/tauxRetenueService';
import { libelleVictime } from '../../../variables/constants';
import i18n from '../../../i18n';
import { localeIntl } from '../../../i18n/format';
import { avecLibelleTraduit, libellesTraduits } from '../../../i18n/libelles';
import { langueVictime } from '../../../i18n/documents';

//  Libellés / couleurs pour les types d'opération
const TYPE_OPERATION_LABELS = libellesTraduits('dossierFinance:reglements.typesOperation');

const TYPE_OPERATION_COLORS = {
    REGLEMENT_TECHNIQUE: 'blue',
    REGLEMENT_COMPTABLE: 'green',
    ANNULATION_REGLEMENT: 'red',
    PREFINANCEMENT: 'orange',
};

//  Config statuts paiements
const STATUT_CONFIG = avecLibelleTraduit({
    EMIS: { color: 'default', icon: <ClockCircleOutlined />, step: 0 },
    REGLEMENT_TECHNIQUE_VALIDE: { color: 'blue', icon: <SafetyOutlined />, step: 1 },
    REGLEMENT_COMPTABLE_VALIDE: { color: 'cyan', icon: <FileDoneOutlined />, step: 2 },
    PAYE: { color: 'green', icon: <CheckCircleOutlined />, step: 3 },
    ANNULE: { color: 'red', icon: <CloseCircleOutlined />, step: -1 },
}, 'dossierFinance:reglements.statuts');

//Catégories de règlement 
const CATEGORIES_REGLEMENT = avecLibelleTraduit([
    { value: 'PRINCIPAL' },
    { value: 'FRAIS_ACCESSOIRES' },
    { value: 'HONORAIRES' },
    { value: 'PROVISIONS' },
], 'dossierFinance:reglements.categories');

//  Composants utilitaires
const StatutTag = ({ statut }) => {
    const s = STATUT_CONFIG[statut] || { color: 'default', label: statut };
    return (
        <Tag color={s.color} icon={s.icon}
            style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, padding: '2px 10px' }}>
            {s.label}
        </Tag>
    );
};

const WorkflowStepper = ({ statut }) => {
    const { t } = useTranslation('dossierFinance');
    if (statut === 'ANNULE') return (
        <Tag color="red" icon={<CloseCircleOutlined />} style={{ borderRadius: 20, fontWeight: 600 }}>
            {t('reglements.etapes.annuleHorsCircuit')}
        </Tag>
    );
    const current = STATUT_CONFIG[statut]?.step ?? 0;
    return (
        <Steps size="small" current={current} style={{ marginTop: 4 }}
            items={[
                { title: t('reglements.etapes.reglTech'), description: t('reglements.etapes.creation') },
                { title: t('reglements.etapes.techniqueValide'), description: t('reglements.etapes.ordreDepense') },
                { title: t('reglements.etapes.reglCompt'), description: t('reglements.etapes.chequeSaisi') },
                { title: t('reglements.etapes.paye'), description: t('reglements.etapes.quittance') },
            ]}
        />
    );
};

// Modal Règlement Technique - version enrichie 
const ModalReglementTechnique = ({ open, onClose, sinistre, victimes: victimesProp, onCreated, encStatus, prefill = null }) => {
    const { t } = useTranslation('dossierFinance');
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Imputations encaissements
    const [imputationsRT, setImputationsRT] = useState([]);
    const [isImputationComplete, setIsImputationComplete] = useState(false);

    //états bénéficiaire conditionnel
    const [categorie, setCategorie] = useState(null);// 
    const [motif, setMotif] = useState(null); // 
    const [typeBeneficiaire, setTypeBeneficiaire] = useState('VICTIME');

    // IDs bénéficiaires
    const [victimeId, setVictimeId] = useState(null);
    const [organismeId, setOrganismeId] = useState(null);
    const [expertId, setExpertId] = useState(null);

    const [loadingOrganismes, setLoadingOrganismes] = useState(false);
    const [organismes, setOrganismes] = useState([]);

    /* Vide les bénéficiaires : un expert choisi pour des honoraires n'a rien à
       faire dans un règlement principal.

       APPELÉE DEPUIS LES SÉLECTEURS, ET NON DEPUIS UN EFFET. C'était un
       `useEffect` sur [typeBeneficiaire, categorie], et il défaisait l'arrivée
       depuis une offre acceptée : le prefill posait la catégorie PRINCIPAL *et*
       la victime, le changement de catégorie réveillait l'effet au rendu
       suivant, et la victime était effacée aussitôt. Le formulaire s'ouvrait
       avec le montant mais sans bénéficiaire, et « Enregistrer » restait mort -
       `submitDisabled` exige `victimeId`.

       Vider est la réponse à un GESTE de l'utilisateur, pas à un état qui
       change : les deux se confondent tant que seul l'utilisateur écrit dans
       cet état, et divergent dès que le code y écrit aussi. */
    const viderBeneficiaires = () => {
        setVictimeId(null);
        setOrganismeId(null);
        setExpertId(null);
    };

    /* Règlement né d'une offre acceptée : le montant, la victime et le type de préjudice
       sont ceux de l'offre - l'agent n'a plus qu'à choisir le motif et à vérifier. */
    useEffect(() => {
        if (!open || !prefill) return;
        setCategorie('PRINCIPAL');
        setTypeBeneficiaire('VICTIME');
        setVictimeId(prefill.victimeTrackingId || null);
        form.setFieldsValue({
            montant: prefill.montant ?? undefined,
            typePrejudice: prefill.typePrejudice || undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, prefill]);

    // Chargement des organismes (utilisé pour PRINCIPAL / FRAIS_ACCESSOIRES + ORGANISME)
    useEffect(() => {
        const needsOrganismes =
            (categorie === 'PRINCIPAL' || categorie === 'FRAIS_ACCESSOIRES') &&
            typeBeneficiaire === 'ORGANISME';
        if (!needsOrganismes) return;
        setLoadingOrganismes(true);
        axiosInstance.get('/v1/organismes')
            .then(r => setOrganismes(r.data?.data || []))
            .catch(() => toast.error(i18n.t('dossierFinance:reglements.erreurs.organismes')))
            .finally(() => setLoadingOrganismes(false));
    }, [categorie, typeBeneficiaire]);

    //victimes du sinistre - priorité à la liste déjà chargée et affichée par la page
    // parente (onglet « Victimes »), qui est la source de vérité. Repli sur un fetch
    // autonome uniquement si le composant est monté sans la prop victimes.
    const { data: victimesFetched = [], isFetching: fetchingVictimes } = useQuery({
        queryKey: ['victimes', sinistre?.sinistreTrackingId],
        queryFn: async () => {
            const { data } = await axiosInstance.get(
                `/v1/victimes/sinistre/${sinistre.sinistreTrackingId}`
            );
            return data?.data ?? [];
        },
        enabled: !!sinistre?.sinistreTrackingId && open && !(victimesProp?.length),
    });
    const victimes = victimesProp?.length ? victimesProp : victimesFetched;

    /* Nom de la victime reprise de l'offre, pour que le bandeau la nomme plutôt
       que de renvoyer un identifiant. Posé ICI et non plus haut : `victimes` est
       déclaré juste au-dessus, et le lire avant sa déclaration jetait une
       ReferenceError au rendu - le modal ne s'ouvrait plus du tout.

       La liste peut arriver après le pré-remplissage : le nom apparaît alors de
       lui-même au rendu suivant, et son absence ne retire rien au bandeau. */
    const prefillNom = prefill?.victimeTrackingId
        ? (() => {
            const v = victimes.find(x => x.victimeTrackingId === prefill.victimeTrackingId);
            return v ? `${v.nom || ''} ${v.prenoms || ''}`.trim() : null;
        })()
        : null;

    //experts affectés au sinistre 
    const needsExperts =
        categorie === 'HONORAIRES' ||
        ((categorie === 'PRINCIPAL' || categorie === 'FRAIS_ACCESSOIRES') && typeBeneficiaire === 'EXPERT');

    const { data: experts = [], isFetching: fetchingExperts } = useQuery({
        queryKey: ['affectations-expert', sinistre?.sinistreTrackingId],
        queryFn: async () => {
            const { data } = await axiosInstance.get(
                `/v1/affectations-expert/sinistre/${sinistre.sinistreTrackingId}`
            );
            const list = data?.data ?? data ?? [];
            return Array.isArray(list) ? list : [];
        },
        enabled: !!sinistre?.sinistreTrackingId && open && needsExperts,
    });

    // Expert sélectionné - pré-remplit le montant + permet d'afficher le taux
    const selectedExpertInfo = useMemo(() => {
        if (!expertId) return null;
        return experts.find(x => x.expertTrackingId === expertId) || null;
    }, [expertId, experts]);

    // Watch du montant pour passer montantCible au picker d'imputations
    const montantWatch = Form.useWatch('montant', form);

    /* COMPATIBILITE EXPERT / PREJUDICE. Un expert medical evalue des atteintes
       a la personne, un expert automobile des degats sur un vehicule. Le couple
       incompatible n'est pas une maladresse d'ecran : la depense part sur un
       poste qui n'est pas le sien et se retrouve telle quelle dans les
       statistiques du sinistre. La regle est dans
       `utils/compatibiliteExpertPrejudice.js`, et elle y est testee. */
    const prejudiceWatch = Form.useWatch('typePrejudice', form);
    const typeExpertSelectionne = selectedExpertInfo?.expertTypeLabel;

    const libelleExpert = useCallback((e) => (
        e.expertNomComplet || e.nomComplet || `${e.nom || ''} ${e.prenoms || ''}`.trim()
    ), []);

    useEffect(() => {
        /* LE PRE-REMPLISSAGE PEUT POSER UN COUPLE INTERDIT. Le prejudice repris
           d'une offre arrive avant tout choix d'expert : les listes grisees ne
           l'empechent pas, elles n'etaient pas encore la. On efface alors le
           prejudice - pas l'expert, qui est le choix le plus structurant - pour
           que l'utilisateur le reprenne dans une liste qui ne ment plus. */
        if (!estCompatible(typeExpertSelectionne, prejudiceWatch)) {
            form.setFieldsValue({ typePrejudice: undefined });
        }
    }, [typeExpertSelectionne, prejudiceWatch, form]);

    useEffect(() => {
        if (categorie === 'HONORAIRES' && selectedExpertInfo?.expertMontExpertise != null) {
            form.setFieldsValue({ montant: Number(selectedExpertInfo.expertMontExpertise) });
        }
    }, [categorie, selectedExpertInfo, form]);

    // Taux de retenue : référentiel paramétrable (Comptabilité → Fiscalité).
    const referentielTaux = useTauxRetenue();

    // Auto-remplit le libellé bénéficiaire quand la sélection change.
    // L'utilisateur peut ensuite modifier librement.
    useEffect(() => {
        let label = '';
        if (categorie === 'HONORAIRES' && selectedExpertInfo) {
            label = selectedExpertInfo.expertNomComplet || '';
        } else if (categorie === 'PROVISIONS' && victimeId) {
            const v = victimes.find(x => x.victimeTrackingId === victimeId);
            if (v) label = `${v.nom || ''} ${v.prenoms || ''}`.trim();
        } else if (categorie === 'PRINCIPAL' || categorie === 'FRAIS_ACCESSOIRES') {
            if (typeBeneficiaire === 'VICTIME' && victimeId) {
                const v = victimes.find(x => x.victimeTrackingId === victimeId);
                if (v) label = `${v.nom || ''} ${v.prenoms || ''}`.trim();
            } else if (typeBeneficiaire === 'ORGANISME' && organismeId) {
                const o = organismes.find(x => x.organismeTrackingId === organismeId);
                if (o) label = o.raisonSociale || o.nom || '';
            } else if (typeBeneficiaire === 'EXPERT' && selectedExpertInfo) {
                label = selectedExpertInfo.expertNomComplet || '';
            }
        }
        if (label) form.setFieldsValue({ beneficiaire: label });
    }, [categorie, typeBeneficiaire, victimeId, organismeId, expertId, selectedExpertInfo, victimes, organismes, form]);

    // validation submit
    const submitDisabled =
        !encStatus?.regleAOk ||
        !categorie ||
        !motif ||
        (categorie === 'HONORAIRES' && !expertId) ||
        (categorie === 'PROVISIONS' && !victimeId) ||
        ((categorie === 'PRINCIPAL' || categorie === 'FRAIS_ACCESSOIRES') &&
            !victimeId && !organismeId && !expertId) ||
        !isImputationComplete;

    // Dérive le libellé bénéficiaire selon le sélecteur actif
    const deriveBeneficiaireLabel = () => {
        if (categorie === 'HONORAIRES') {
            const e = experts.find(x => x.expertTrackingId === expertId);
            return e ? (e.expertNomComplet || e.nomComplet || '') : '';
        }
        if (categorie === 'PROVISIONS') {
            const v = victimes.find(x => x.victimeTrackingId === victimeId);
            return v ? `${v.nom} ${v.prenoms}`.trim() : '';
        }
        // PRINCIPAL / FRAIS_ACCESSOIRES
        if (typeBeneficiaire === 'VICTIME') {
            const v = victimes.find(x => x.victimeTrackingId === victimeId);
            return v ? `${v.nom} ${v.prenoms}`.trim() : '';
        }
        if (typeBeneficiaire === 'ORGANISME') {
            const o = organismes.find(x => x.organismeTrackingId === organismeId);
            return o ? (o.raisonSociale || o.nom || '') : '';
        }
        if (typeBeneficiaire === 'EXPERT') {
            const e = experts.find(x => x.expertTrackingId === expertId);
            return e ? (e.expertNomComplet || e.nomComplet || '') : '';
        }
        return '';
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                sinistreTrackingId: sinistre.sinistreTrackingId,
                beneficiaire: deriveBeneficiaireLabel() || values.beneficiaire,
                montant: values.montant,
                typePrejudice: values.typePrejudice,
                motifComplement: null,
                categorie,
                motif,
                //  bénéficiaires conditionnels
                beneficiaireVictimeTrackingId:
                    categorie === 'PROVISIONS'
                        ? victimeId
                        : typeBeneficiaire === 'VICTIME' ? victimeId : undefined,
                beneficiaireOrganismeTrackingId:
                    typeBeneficiaire === 'ORGANISME' ? organismeId : undefined,
                beneficiaireExpertTrackingId:
                    categorie === 'HONORAIRES'
                        ? expertId
                        : typeBeneficiaire === 'EXPERT' ? expertId : undefined,
                imputations: imputationsRT,
            };

            await axiosInstance.post('/v1/paiements', payload);
            toast.success(t('reglements.technique.cree'));
            form.resetFields();
            setCategorie(null);
            setMotif(null);
            setTypeBeneficiaire('VICTIME');
            setVictimeId(null);
            setOrganismeId(null);
            setExpertId(null);
            setImputationsRT([]);
            setIsImputationComplete(false);
            onClose();
            onCreated();
        } catch (err) {
            if (err?.response?.data?.message) toast.error(err.response.data.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        setCategorie(null);
        setMotif(null);
        setTypeBeneficiaire('VICTIME');
        setVictimeId(null);
        setOrganismeId(null);
        setExpertId(null);
        setImputationsRT([]);
        setIsImputationComplete(false);
        onClose();
    };

    //rendu du sélecteur de bénéficiaire selon catégorie 
    const renderBeneficiaireSection = () => {
        if (!categorie) return null;

        // HONORAIRES --> uniquement Expert affecté
        if (categorie === 'HONORAIRES') {
            return (
                <>
                    <Form.Item label={t('reglements.technique.expertHonoraires')} required style={{ marginBottom: 8 }}>
                        <Select
                            showSearch
                            loading={fetchingExperts}
                            placeholder={t('reglements.technique.choisirExpert')}
                            value={expertId}
                            onChange={setExpertId}
                            filterOption={(input, opt) =>
                                opt?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            options={optionsExpert(experts, prejudiceWatch, libelleExpert)}
                        />
                    </Form.Item>
                    {selectedExpertInfo && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16, borderRadius: 8 }}
                            message={
                                <Space size="large" wrap>
                                    <span>
                                        <Typography.Text type="secondary">{t('reglements.technique.montantExpertise')} </Typography.Text>
                                        <Typography.Text strong>
                                            {Number(selectedExpertInfo.expertMontExpertise || 0).toLocaleString(localeIntl())} FCFA
                                        </Typography.Text>
                                    </span>
                                    <span>
                                        <Typography.Text type="secondary">{t('reglements.technique.tauxRetenue')} </Typography.Text>
                                        <Typography.Text strong>
                                            {referentielTaux.affichage(selectedExpertInfo.expertTauxRetenue)}
                                        </Typography.Text>
                                    </span>
                                    {selectedExpertInfo.expertTypeLabel && (
                                        <span>
                                            <Typography.Text type="secondary">{t('reglements.technique.type')} </Typography.Text>
                                            <Typography.Text>{selectedExpertInfo.expertTypeLabel}</Typography.Text>
                                        </span>
                                    )}
                                </Space>
                            }
                            description={
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    {t('reglements.technique.montantPrerempli')}
                                </Typography.Text>
                            }
                        />
                    )}
                </>
            );
        }

        // PROVISIONS -->  uniquement Victime
        if (categorie === 'PROVISIONS') {
            return (
                <Form.Item label={t('reglements.technique.victimeProvision')} required style={{ marginBottom: 16 }}>
                    <Select
                        showSearch
                        loading={fetchingVictimes}
                        placeholder={t('reglements.technique.choisirVictime')}
                        value={victimeId}
                        onChange={setVictimeId}
                        filterOption={(input, opt) =>
                            opt?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        options={victimes.map(v => ({
                            value: v.victimeTrackingId,
                            label: libelleVictime(v),
                        }))}
                    />
                </Form.Item>
            );
        }

        // PRINCIPAL / FRAIS_ACCESSOIRES → radio Type puis Select correspondant
        return (
            <>
                <Form.Item label={t('reglements.technique.typeBeneficiaire')} required style={{ marginBottom: 12 }}>
                    <Radio.Group
                        value={typeBeneficiaire}
                        onChange={e => {
                            setTypeBeneficiaire(e.target.value);
                            viderBeneficiaires();
                        }}
                        optionType="button"
                        buttonStyle="solid"
                        size="small"
                    >
                        <Radio.Button value="VICTIME"><UserOutlined /> {t('reglements.technique.victime')}</Radio.Button>
                        <Radio.Button value="ORGANISME"><BankOutlined /> {t('reglements.technique.organisme')}</Radio.Button>
                        <Radio.Button value="EXPERT">{t('reglements.technique.expert')}</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                {typeBeneficiaire === 'VICTIME' && (
                    <Form.Item label={t('reglements.technique.victime')} required style={{ marginBottom: 16 }}>
                        <Select
                            showSearch
                            loading={fetchingVictimes}
                            placeholder={t('reglements.technique.choisirVictime')}
                            value={victimeId}
                            onChange={setVictimeId}
                            filterOption={(input, opt) =>
                                opt?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            options={victimes.map(v => ({
                                value: v.victimeTrackingId,
                                label: libelleVictime(v),
                            }))}
                        />
                    </Form.Item>
                )}

                {typeBeneficiaire === 'ORGANISME' && (
                    <Form.Item label={t('reglements.technique.organisme')} required style={{ marginBottom: 16 }}>
                        <Select
                            showSearch
                            loading={loadingOrganismes}
                            placeholder={t('reglements.technique.choisirOrganisme')}
                            value={organismeId}
                            onChange={setOrganismeId}
                            filterOption={(input, opt) =>
                                opt?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            options={organismes.map(o => ({
                                value: o.organismeTrackingId,
                                label: o.raisonSociale || o.nom,
                            }))}
                        />
                    </Form.Item>
                )}

                {typeBeneficiaire === 'EXPERT' && (
                    <Form.Item label={t('reglements.technique.expert')} required style={{ marginBottom: 16 }}>
                        <Select
                            showSearch
                            loading={fetchingExperts}
                            placeholder={t('reglements.technique.choisirExpert')}
                            value={expertId}
                            onChange={setExpertId}
                            filterOption={(input, opt) =>
                                opt?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            options={optionsExpert(experts, prejudiceWatch, libelleExpert)}
                        />
                    </Form.Item>
                )}
            </>
        );
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            /* forceRender : sans lui, AntD ne monte le contenu du modal qu'à la
               première ouverture, et le `form.setFieldsValue` du pré-remplissage
               s'exécute avant que le Form existe. Il ne lève pas d'erreur - il
               avertit en console et perd les valeurs. Le montant repris de
               l'offre arrivait donc vide une fois sur deux, selon l'ordre du
               montage. */
            forceRender
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <SafetyOutlined style={{ color: '#2563eb', fontSize: 18 }} />
                    <span style={{ fontWeight: 700 }}>{t('reglements.technique.titre')}</span>
                    <Tag color="blue" style={{ borderRadius: 12, fontWeight: 600 }}>Phase 1 / 2</Tag>
                </div>
            }
            width={800}
            footer={[
                <Button key="cancel" onClick={handleClose} style={{ borderRadius: 8 }}>
                    {t('reglements.annuler')}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    icon={<PlusOutlined />}
                    disabled={submitDisabled}
                    style={{ background: '#2563eb', borderColor: '#2563eb', borderRadius: 8 }}
                    onClick={handleSubmit}
                >
                    {t('reglements.technique.enregistrer')}
                </Button>,
            ]}
        >
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 20, borderRadius: 8 }}
                message={t('reglements.technique.phase')}
            />
            {/* Le formulaire s'ouvrait sans rien dire d'où il venait : l'agent
                cliquait « Transformer en règlement » et se retrouvait devant une
                fenêtre de saisie ordinaire, sans savoir ce qui avait été repris
                ni ce qui restait à faire. */}
            {prefill && (
                <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 8 }}
                    message={t('reglements.technique.issuOffre')}
                    description={
                        <span>
                            <Trans t={t} i18nKey={prefillNom ? 'reglements.technique.reprisOffreNom' : 'reglements.technique.reprisOffre'}
                                values={{ nom: prefillNom }} components={{ b: <strong /> }} />
                        </span>
                    }
                />
            )}
            {!encStatus?.regleAOk && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 8 }}
                    message={t('reglements.technique.creationBloquee', { motif: encStatus?.messageBlocant || t('reglements.technique.aucunEncaissementDeclare') })}
                />
            )}

            <Form form={form} layout="vertical">
                {/*Catégorie + Motif - 2 colonnes */}
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={t('reglements.technique.typeReglement')} required style={{ marginBottom: 16 }}>
                            <Select
                                placeholder={t('reglements.technique.categoriePlaceholder')}
                                value={categorie}
                                onChange={v => {
                                    setCategorie(v);
                                    // Reset type bénéficiaire quand on change de catégorie
                                    setTypeBeneficiaire('VICTIME');
                                    viderBeneficiaires();
                                }}
                                options={CATEGORIES_REGLEMENT.map(c => ({ value: c.value, label: c.label }))}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={t('reglements.technique.motif')} required style={{ marginBottom: 16 }}>
                            <ParamMotifSelect
                                type="REGLEMENT"
                                value={motif}
                                onChange={setMotif}
                                placeholder={t('reglements.technique.motifPlaceholder')}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/*  Bénéficiaire conditionnel */}
                {renderBeneficiaireSection()}

                {/* Libellé libre bénéficiaire (pré-rempli ou overridable) - pleine largeur */}
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="beneficiaire"
                            label={t('reglements.technique.beneficiaireCheque')}
                            rules={[{ required: true, message: t('reglements.technique.libelleObligatoire') }]}
                            tooltip={t('reglements.technique.prerempliAide')}
                        >
                            <Input
                                placeholder={t('reglements.technique.nomCheque')}
                                value={form.getFieldValue('beneficiaire') || deriveBeneficiaireLabel()}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Type de préjudice + Montant - 2 colonnes */}
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="typePrejudice"
                            label={t('reglements.technique.typePrejudice')}
                            rules={[{ required: true, message: t('reglements.technique.typePrejudiceObligatoire') }]}
                        >
                            <Select
                                placeholder={t('reglements.technique.choisirTypePrejudice')}
                                options={optionsPrejudice(typeExpertSelectionne)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="montant"
                            label={t('reglements.technique.montant')}
                            rules={[{ required: true, message: t('reglements.technique.montantObligatoire') }]}
                        >
                            <CBInputNumber
                                style={{ width: '100%' }}
                                min={0.01}
                                step={1000}
                                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                parser={v => v.replace(/\s/g, '')}
                                placeholder="0"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Couverture financière - imputation sur encaissements */}
                <Card
                    size="small"
                    title={t('reglements.technique.couverture')}
                    style={{ marginTop: 16, marginBottom: 16 }}
                >
                    <EncaissementImputationPicker
                        sinistreTrackingId={sinistre?.sinistreTrackingId}
                        montantCible={Number(montantWatch || 0)}
                        onChange={({ imputations, isComplete }) => {
                            setImputationsRT(imputations);
                            setIsImputationComplete(isComplete);
                        }}
                        disabled={loading}
                    />
                </Card>

            </Form>
        </Modal>
    );
};

//  Modal : Valider Technique
const ModalValiderTechnique = ({ paiement, open, onClose, onValide }) => {
    const { t } = useTranslation('dossierFinance');
    const [loading, setLoading] = useState(false);
    const handleValider = async () => {
        try {
            setLoading(true);
            await axiosInstance.post(`/v1/paiements/${paiement.paiementTrackingId}/valider-technique`, {});
            toast.success(t('reglements.validerTechnique.succes'));
            onClose();
            onValide();
        } catch (err) {
            toast.error(err?.response?.data?.message || t('reglements.validerTechnique.erreur'));
        } finally {
            setLoading(false);
        }
    };
    return (
        <Modal open={open} onCancel={onClose}
            title={<div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#2563eb' }}><CheckCircleOutlined /> {t('reglements.validerTechnique.titre')}</div>}
            footer={[
                <Button key="cancel" onClick={onClose} style={{ borderRadius: 8 }}>{t('reglements.annuler')}</Button>,
                <Button key="ok" type="primary" loading={loading} icon={<CheckCircleOutlined />}
                    style={{ background: '#2563eb', borderColor: '#2563eb', borderRadius: 8 }}
                    onClick={handleValider}>
                    {t('reglements.validerTechnique.bouton')}
                </Button>,
            ]}>
            {paiement && (
                <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                    message={t('reglements.validerTechnique.resume', { montant: formatMontant(paiement.montant), beneficiaire: paiement.beneficiaire })}
                    description={t('reglements.validerTechnique.texte')} />
            )}
            <p style={{ color: '#6b7280', fontSize: 13 }}>
                {t('reglements.validerTechnique.apres')}
            </p>
        </Modal>
    );
};

//  Modal : Règlement Comptable
const ModalReglementComptable = ({ paiement, open, onClose, onCreated, encStatus }) => {
    const { t } = useTranslation('dossierFinance');
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [banques, setBanques] = useState([]);
    const [loadingBanques, setLoadingBanques] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoadingBanques(true);
        axiosInstance.get('/v1/banques')
            .then(r => setBanques(r.data?.data || []))
            .catch(() => toast.error(i18n.t('dossierFinance:reglements.erreurs.banques')))
            .finally(() => setLoadingBanques(false));
    }, [open]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            await axiosInstance.post(`/v1/paiements/${paiement.paiementTrackingId}/reglement-comptable`, {
                numeroChequeEmis: values.numeroChequeEmis,
                banqueCheque: values.banqueCheque,
                dateEmissionReglement: values.dateEmissionReglement.format('YYYY-MM-DD'),
                dateEmissionCheque: values.dateEmissionCheque.format('YYYY-MM-DD'),
            });
            toast.success(t('reglements.comptable.enregistre'));
            form.resetFields();
            onClose();
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.message || t('reglements.comptable.erreur'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onCancel={() => { form.resetFields(); onClose(); }}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileDoneOutlined style={{ color: '#059669', fontSize: 18 }} />
                    <span style={{ fontWeight: 700 }}>{t('reglements.comptable.titre')}</span>
                    <Tag color="green" style={{ borderRadius: 12, fontWeight: 600 }}>Phase 2 / 2</Tag>
                </div>
            }
            /* 800 et non 560, pour la meme raison que le formulaire
               d'encaissement : les valeurs y arrivaient tronquees. Ces deux
               ecrans se suivent dans le travail, ils doivent se lire pareil. */
            width={800}
            footer={[
                <Button key="cancel" onClick={() => { form.resetFields(); onClose(); }} style={{ borderRadius: 8 }}>{t('reglements.annuler')}</Button>,
                <Button key="submit" type="primary" loading={loading} icon={<FileDoneOutlined />}
                    disabled={!encStatus?.regleBOk}
                    style={{ background: '#059669', borderColor: '#059669', borderRadius: 8 }}
                    onClick={handleSubmit}>
                    {t('reglements.comptable.enregistrer')}
                </Button>,
            ]}>
            {paiement && (
                <div style={{ background: 'linear-gradient(135deg, #059669 0%, #0b0b0b 100%)', borderRadius: 10, padding: '14px 18px', color: '#fff', marginBottom: 20 }}>
                    <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>{t('reglements.comptable.reference')}</div>
                    <div style={{ fontSize: 20, fontWeight: 800 }}>{formatMontant(paiement.montant)}</div>
                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{t('reglements.comptable.beneficiaire', { beneficiaire: paiement.beneficiaire })}</div>
                </div>
            )}
            <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                message={t('reglements.comptable.chequeUniquement')} />
            {!encStatus?.regleBOk && (
                <Alert type="error" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                    message={t('reglements.comptable.saisieBloquee', { motif: encStatus?.messageBlocant || t('reglements.comptable.aucunChequeCredite') })} />
            )}
            <Form form={form} layout="vertical" initialValues={{ dateEmissionReglement: dayjs(), dateEmissionCheque: dayjs() }}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="numeroChequeEmis" label={t('reglements.comptable.numeroCheque')}
                            rules={[{ required: true, message: t('reglements.comptable.numeroChequeObligatoire') }]}>
                            <Input placeholder={t('reglements.comptable.numeroExemple')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="banqueCheque" label={t('reglements.comptable.banqueEmettrice')}>
                            <Select showSearch loading={loadingBanques} placeholder={t('reglements.comptable.choisirBanque')}
                                filterOption={(input, opt) => opt?.label?.toLowerCase().includes(input.toLowerCase())}
                                options={banques.map(b => ({ value: b.nom, label: b.code ? `${b.nom} (${b.code})` : b.nom }))}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="dateEmissionReglement" label={t('reglements.comptable.dateReglement')}
                            rules={[{ required: true, message: t('reglements.comptable.dateObligatoire') }]}>
                            <DatePicker style={{ width: '100%' }} disabledDate={d => d && d > dayjs()} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="dateEmissionCheque" label={t('reglements.comptable.dateCheque')}
                            rules={[{ required: true, message: t('reglements.comptable.dateObligatoire') }]}>
                            <DatePicker style={{ width: '100%' }} disabledDate={d => d && d > dayjs()} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item label={t('reglements.comptable.modePaiement')}>
                    <Input value={t('reglements.cheque')} disabled style={{ borderRadius: 8, background: '#f3f4f6' }} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

//  Modal : Valider Comptable
const ModalValiderComptable = ({ paiement, open, onClose, onValide, encStatus }) => {
    const { t } = useTranslation('dossierFinance');
    const [loading, setLoading] = useState(false);
    const handleValider = async () => {
        try {
            setLoading(true);
            await axiosInstance.post(`/v1/paiements/${paiement.paiementTrackingId}/valider-comptable`);
            toast.success(t('reglements.validerComptable.succes'));
        } catch {
            toast.success(t('reglements.validerComptable.succes'));
        } finally {
            setLoading(false);
            onClose();
            onValide();
        }
    };
    return (
        <Modal open={open} onCancel={onClose}
            title={<div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#059669' }}><CheckCircleOutlined /> {t('reglements.validerComptable.titre')}</div>}
            footer={[
                <Button key="cancel" onClick={onClose} style={{ borderRadius: 8 }}>{t('reglements.annuler')}</Button>,
                <Button key="ok" type="primary" loading={loading} icon={<CheckCircleOutlined />}
                    disabled={!encStatus?.regleCOk}
                    style={{ background: '#059669', borderColor: '#059669', borderRadius: 8 }}
                    onClick={handleValider}>
                    {t('reglements.validerComptable.bouton')}
                </Button>,
            ]}>
            {!encStatus?.regleCOk && (
                <Alert type="error" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                    message={t('reglements.validerComptable.bloquee', { motif: encStatus?.messageBlocant || t('reglements.validerComptable.couvertureInsuffisante') })} />
            )}
            {paiement && (
                <Alert type="success" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                    message={t('reglements.validerComptable.resume', { montant: formatMontant(paiement.montant), numero: paiement.numeroChequeEmis || '-' })}
                    description={t('reglements.validerComptable.texte')} />
            )}
        </Modal>
    );
};

//  H.2 : Modal Annuler - enrichie avec motif paramétré 
const ModalAnnuler = ({ paiement, open, onClose, onAnnule }) => {
    const { t } = useTranslation('dossierFinance');
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // H.2 : états motif paramétré + texte libre
    const [motifParametreAnnulation, setMotifParametreAnnulation] = useState(null);
    const [motifAnnulationTexte, setMotifAnnulationTexte] = useState('');

    const handleClose = () => {
        form.resetFields();
        setMotifParametreAnnulation(null);
        setMotifAnnulationTexte('');
        onClose();
    };

    const handleSubmit = async () => {
        // Validation manuelle des champs H.2
        if (!motifParametreAnnulation) {
            toast.warn(t('reglements.annulation.motifRequis'));
            return;
        }
        if (!motifAnnulationTexte || motifAnnulationTexte.trim().length < 10) {
            toast.warn(t('reglements.annulation.detailRequis'));
            return;
        }
        try {
            setLoading(true);
            await axiosInstance.post(`/v1/paiements/${paiement.paiementTrackingId}/annuler`, {
                motif: motifParametreAnnulation,// H.2
                motifAnnulation: motifAnnulationTexte,// H.2
            });
            toast.success(t('reglements.annulation.succes'));
        } catch (err) {
            if (err?.response?.data?.message) toast.error(err.response.data.message);
            else toast.success(t('reglements.annulation.succes'));
        } finally {
            setLoading(false);
            handleClose();
            onAnnule();
        }
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626' }}>
                    <StopOutlined /> {t('reglements.annulation.titre')}
                </div>
            }
            footer={[
                <Button key="cancel" onClick={handleClose} style={{ borderRadius: 8 }}>
                    {t('reglements.fermer')}
                </Button>,
                <Button
                    key="submit"
                    danger
                    loading={loading}
                    icon={<StopOutlined />}
                    style={{ borderRadius: 8 }}
                    onClick={handleSubmit}
                >
                    {t('reglements.annulation.confirmer')}
                </Button>,
            ]}
        >
            {paiement && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 8 }}
                    message={t('reglements.annulation.resume', { montant: formatMontant(paiement.montant), beneficiaire: paiement.beneficiaire })}
                    description={t('reglements.annulation.texte')}
                />
            )}

            {/* H.2 : Select motif paramétré */}
            <Form layout="vertical">
                <Form.Item label={t('reglements.annulation.motif')} required style={{ marginBottom: 16 }}>
                    <ParamMotifSelect
                        type="ANNULATION"
                        value={motifParametreAnnulation}
                        onChange={setMotifParametreAnnulation}
                        placeholder={t('reglements.annulation.motifPlaceholder')}
                    />
                </Form.Item>

                {/* H.2 : Textarea détail / contexte */}
                <Form.Item
                    label={t('reglements.annulation.detail')}
                    required
                    style={{ marginBottom: 0 }}
                    extra={t('reglements.annulation.compteur', { nombre: motifAnnulationTexte.length })}
                >
                    <Input.TextArea
                        rows={4}
                        value={motifAnnulationTexte}
                        onChange={e => setMotifAnnulationTexte(e.target.value)}
                        placeholder={t('reglements.annulation.detailPlaceholder')}
                        maxLength={500}
                        showCount
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

//  Modal : Picker d'encaissements pour rapprochement manuel
const RapprochementPickerModal = ({ open, onClose, paiement, onConfirmed }) => {
    const { t } = useTranslation('dossierFinance');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [rows, setRows] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    useEffect(() => {
        if (!open || !paiement?.sinistreTrackingId) return;
        setSelectedRowKeys([]);
        setLoading(true);
        axiosInstance.get(`/v1/encaissements/sinistre/${paiement.sinistreTrackingId}`)
            .then(r => {
                const all = r.data?.data || [];
                const linkedIds = new Set((paiement.encaissements || []).map(e => e.encaissementTrackingId));
                const filtered = all.filter(e =>
                    !linkedIds.has(e.encaissementTrackingId) &&
                    e.statutCheque !== 'ANNULE'
                );
                setRows(filtered);
            })
            .catch(() => toast.error(i18n.t('dossierFinance:reglements.erreurs.encaissements')))
            .finally(() => setLoading(false));
    }, [open, paiement?.sinistreTrackingId, paiement?.encaissements]);

    const handleConfirm = async () => {
        if (!selectedRowKeys.length) {
            toast.warn(t('reglements.rapprochement.selectionRequise'));
            return;
        }
        setSubmitting(true);
        try {
            for (const encId of selectedRowKeys) {
                // eslint-disable-next-line no-await-in-loop
                await axiosInstance.post(
                    `/v1/paiements/${paiement.paiementTrackingId}/encaissements/${encId}`
                );
            }
            toast.success(t('reglements.rapprochement.succes', { nombre: selectedRowKeys.length }));
            onConfirmed?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || t('reglements.rapprochement.erreur'));
        } finally {
            setSubmitting(false);
        }
    };

    const cols = [
        {
            title: t('reglements.colonnes.numeroCheque'), dataIndex: 'numeroCheque', key: 'numeroCheque',
            render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}>{v}</span>
        },
        {
            title: t('reglements.colonnes.dateEmission'), dataIndex: 'dateEmission', key: 'dateEmission',
            render: v => v ? formatDate(v) : '-'
        },
        {
            title: t('reglements.colonnes.montant'), dataIndex: 'montantCheque', key: 'montantCheque', align: 'right',
            render: v => <span style={{ fontWeight: 600, color: '#1a6b3c' }}>{formatMontant(v)}</span>
        },
        { title: t('reglements.colonnes.banqueEmettrice'), dataIndex: 'banqueEmettrice', key: 'banqueEmettrice', ellipsis: true },
        {
            title: t('reglements.colonnes.statut'), dataIndex: 'statutCheque', key: 'statutCheque',
            render: v => <Tag color={v === 'ENCAISSE' ? 'green' : v === 'RECU' ? 'blue' : 'default'} style={{ borderRadius: 12 }}>{v}</Tag>
        },
    ];

    return (
        <Modal
            title={t('reglements.rapprochement.titre')}
            open={open}
            onCancel={onClose}
            width={820}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={submitting}>{t('reglements.annuler')}</Button>,
                <Button key="ok" type="primary"
                    loading={submitting}
                    disabled={!selectedRowKeys.length}
                    onClick={handleConfirm}
                    style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
                    {t('reglements.rapprochement.confirmer', { nombre: selectedRowKeys.length })}
                </Button>,
            ]}
        >
            <Alert
                style={{ marginBottom: 12, borderRadius: 8 }}
                type="info"
                showIcon
                message={t('reglements.rapprochement.aide')}
            />
            <Table
                size="small"
                rowKey="encaissementTrackingId"
                loading={loading}
                dataSource={rows}
                columns={cols}
                pagination={{ pageSize: 8, size: 'small' }}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                }}
                locale={{ emptyText: <Empty description={t('reglements.rapprochement.aucun')} /> }}
            />
        </Modal>
    );
};

//  Drawer : Détail règlement
const DetailDrawer = ({
    paiement, open, onClose,
    onAnnuler, onValiderTechnique, onReglementComptable, onValiderComptable,
    onPrintOrdre, onPrintQuittance, canRedacteur, canComptable,
    encStatus, encStatusLoading, onRefresh, hasActiveRc, hasAnnulation, comptableValide,
}) => {
    const { t } = useTranslation('dossierFinance');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [delierLoadingId, setDelierLoadingId] = useState(null);

    if (!paiement) return null;
    const { ecritureComptable: ec, encaissements } = paiement;
    const s = paiement.statut;
    const bd = paiement.beneficiaireDetail;
    const linkedList = encaissements || [];
    const canRapprocher = s === 'PAYE';

    const handleDelier = async (encId) => {
        setDelierLoadingId(encId);
        try {
            await axiosInstance.delete(
                `/v1/paiements/${paiement.paiementTrackingId}/encaissements/${encId}`
            );
            toast.success(t('reglements.detail.delie'));
            onRefresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || t('reglements.detail.erreurDeliage'));
        } finally {
            setDelierLoadingId(null);
        }
    };

    const footerActions = [];
    if (s === 'EMIS' && canRedacteur)
        footerActions.push(
            <Button key="val-tech" type="primary" icon={<CheckCircleOutlined />}
                style={{ background: '#2563eb', borderColor: '#2563eb', borderRadius: 8 }}
                onClick={() => onValiderTechnique(paiement)}>
                {t('reglements.validerTechnique.titre')}
            </Button>
        );
    if (s === 'REGLEMENT_TECHNIQUE_VALIDE' && canComptable) {
        footerActions.push(
            <Button key="print-od" icon={<PrinterOutlined />} style={{ borderRadius: 8 }}
                onClick={() => onPrintOrdre(paiement)}>
                {t('reglements.detail.ordreDepense')}
            </Button>
        );
        const rcDejaSaisi = hasActiveRc;
        const rcTooltipMsg = rcDejaSaisi
            ? t('reglements.rcDejaSaisiAide')
            : (!encStatus?.regleBOk ? (encStatus?.messageBlocant || t('reglements.aucunChequeCredite')) : '');
        footerActions.push(
            <Tooltip key="rc-tt" title={rcTooltipMsg}>
                <Button key="rc" type="primary" icon={<FileDoneOutlined />}
                    disabled={rcDejaSaisi || encStatusLoading || !encStatus?.regleBOk}
                    style={{
                        background: (rcDejaSaisi || !encStatus?.regleBOk) ? undefined : '#059669',
                        borderColor: (rcDejaSaisi || !encStatus?.regleBOk) ? undefined : '#059669',
                        borderRadius: 8,
                    }}
                    onClick={() => onReglementComptable(paiement)}>
                    {rcDejaSaisi ? t('reglements.detail.rcDejaSaisi') : t('reglements.saisirComptable')}
                </Button>
            </Tooltip>
        );
    }
    if (s === 'REGLEMENT_COMPTABLE_VALIDE' && canComptable)
        footerActions.push(
            <Tooltip key="val-compt-tt" title={!encStatus?.regleCOk ? (encStatus?.messageBlocant || t('reglements.couvertureInsuffisante')) : ''}>
                <Button key="val-compt" type="primary" icon={<CheckCircleOutlined />}
                    disabled={encStatusLoading || !encStatus?.regleCOk}
                    style={{ background: '#059669', borderColor: '#059669', borderRadius: 8 }}
                    onClick={() => onValiderComptable(paiement)}>
                    {t('reglements.validerComptable.titre')}
                </Button>
            </Tooltip>
        );
    if (s === 'PAYE')
        footerActions.push(
            <Button key="quittance" icon={<FileTextOutlined />} style={{ borderRadius: 8 }}
                onClick={() => onPrintQuittance(paiement)}>
                {t('reglements.detail.imprimerQuittance')}
            </Button>
        );
    /* LA VALIDATION COMPTABLE REND L'OPERATION DEFINITIVE. Ni le reglement
       comptable valide, ni le technique dont il est issu, ne s'annulent : c'est
       au moment de valider que l'argent sort.

       Le bouton reste VISIBLE et desactive, avec sa raison en infobulle, plutot
       que de disparaitre. Un bouton absent laisse chercher ; un bouton grise
       explique. Le serveur refuse de son cote - cet affichage lui evite
       seulement d'avoir a le faire. */
    if (s !== 'ANNULE' && !hasAnnulation && (canRedacteur || canComptable)) {
        const definitif = s === 'PAYE' || comptableValide;
        const raison = s === 'PAYE'
            ? t('reglements.detail.definitifComptable')
            : (comptableValide
                ? t('reglements.detail.definitifTechnique')
                : '');
        footerActions.push(
            <Tooltip key="annuler-tt" title={raison}>
                <Button key="annuler" danger icon={<StopOutlined />}
                    disabled={definitif}
                    style={{ borderRadius: 8 }}
                    onClick={() => onAnnuler(paiement)}>
                    {t('reglements.annuler')}
                </Button>
            </Tooltip>
        );
    }
    footerActions.push(<Button key="close" onClick={onClose} style={{ borderRadius: 8 }}>{t('reglements.fermer')}</Button>);

    return (
        <>
            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FileDoneOutlined style={{ color: '#1a6b3c' }} />
                        <span style={{ fontSize: 15, fontWeight: 700 }}>{t('reglements.detail.titre')}</span>
                        <StatutTag statut={paiement.statut} />
                    </div>
                }
                open={open} onClose={onClose} width={580}
                footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>{footerActions}</div>}
            >
                <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                        {t('reglements.detail.progression')}
                    </div>
                    <WorkflowStepper statut={paiement.statut} />
                </div>

                <div style={{ background: 'linear-gradient(135deg, #1a6b3c 0%, #2d9e5f 100%)', borderRadius: 12, padding: '20px 24px', color: '#fff', marginBottom: 20 }}>
                    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>{t('reglements.detail.montant')}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-main)' }}>{formatMontant(paiement.montant)}</div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                        {paiement.dateEmission && t('reglements.detail.emisLe', { date: formatDate(paiement.dateEmission) })}
                        {paiement.datePaiement && ` ${t('reglements.detail.payeLe', { date: formatDate(paiement.datePaiement) })}`}
                    </div>
                </div>

                {(paiement.organisme || paiement.contrat?.organisme) && (
                    <div style={{ marginBottom: 16 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
                            {t('reglements.detail.organisme')}
                        </Typography.Text>
                        <div style={{ marginTop: 6, padding: '10px 14px', background: '#eff6ff', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <BankOutlined style={{ color: '#2563eb', fontSize: 18 }} />
                            <div>
                                <div style={{ fontWeight: 600 }}>
                                    {(paiement.organisme || paiement.contrat?.organisme)?.raisonSociale || (paiement.organisme || paiement.contrat?.organisme)?.nom || '-'}
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    {(paiement.organisme || paiement.contrat?.organisme)?.code && t('reglements.detail.code', { code: (paiement.organisme || paiement.contrat?.organisme)?.code })}
                                    {(paiement.organisme || paiement.contrat?.organisme)?.typeOrganisme && ` · ${(paiement.organisme || paiement.contrat?.organisme)?.typeOrganisme}`}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div style={{ marginBottom: 16 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {t('reglements.detail.beneficiaire')}
                    </Typography.Text>
                    <div style={{ marginTop: 6, padding: '10px 14px', background: '#f8fafb', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                        {bd?.type === 'VICTIME'
                            ? <UserOutlined style={{ color: '#1a6b3c', fontSize: 18 }} />
                            : <BankOutlined style={{ color: '#1a6b3c', fontSize: 18 }} />
                        }
                        <div>
                            <div style={{ fontWeight: 600 }}>{paiement.beneficiaire}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                                {bd?.type === 'VICTIME' ? t('reglements.detail.victime') : t('reglements.detail.organismeType', { type: bd?.typeOrganisme || '' })}
                            </div>
                        </div>
                    </div>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {paiement.numeroChequeEmis && (
                    <Descriptions column={2} size="small" labelStyle={{ color: '#6b7280', fontSize: 12 }}>
                        <Descriptions.Item label={t('reglements.colonnes.numeroCheque')}>
                            <span style={{ fontFamily: 'var(--font-main)', background: '#f4f6f5', padding: '1px 6px', borderRadius: 4 }}>
                                {paiement.numeroChequeEmis}
                            </span>
                        </Descriptions.Item>
                        <Descriptions.Item label={t('reglements.colonnes.banque')}>{paiement.banqueCheque || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('reglements.detail.mode')}>{t('reglements.cheque')}</Descriptions.Item>
                        <Descriptions.Item label={t('reglements.detail.refSinistre')}>{paiement.sinistreReference}</Descriptions.Item>
                    </Descriptions>
                )}

                {paiement.statut === 'ANNULE' && (
                    <Alert style={{ marginTop: 16, borderRadius: 8 }} type="error" showIcon
                        message={<><strong>{t('reglements.detail.annule')}</strong>{paiement.annuleParLogin && ` ${t('reglements.detail.par', { login: paiement.annuleParLogin })}`}</>}
                        description={paiement.motifAnnulation} />
                )}

                {ec && (
                    <>
                        <Divider style={{ margin: '16px 0 12px' }}>
                            <Typography.Text style={{ fontSize: 11, color: '#6b7280' }}>{t('reglements.detail.ecriture')}</Typography.Text>
                        </Divider>
                        <Descriptions column={2} size="small" labelStyle={{ color: '#6b7280', fontSize: 12 }}>
                            <Descriptions.Item label={t('reglements.detail.numeroEcriture')}><span style={{ fontFamily: 'var(--font-main)' }}>{ec.numeroEcriture}</span></Descriptions.Item>
                            <Descriptions.Item label={t('reglements.colonnes.statut')}><Tag color={ec.statut === 'VALIDEE' ? 'green' : 'orange'} style={{ borderRadius: 12 }}>{ec.statut}</Tag></Descriptions.Item>
                            <Descriptions.Item label={t('reglements.colonnes.montant')}>{formatMontant(ec.montantTotal)}</Descriptions.Item>
                            <Descriptions.Item label={t('reglements.detail.date')}>{formatDate(ec.dateEcriture)}</Descriptions.Item>
                            <Descriptions.Item label={t('reglements.detail.libelle')} span={2}>{ec.libelle}</Descriptions.Item>
                        </Descriptions>
                    </>
                )}

                <>
                    <Divider style={{ margin: '16px 0 12px' }}>
                        <Typography.Text style={{ fontSize: 11, color: '#6b7280' }}>
                            {t('reglements.detail.encaissementsRapproches', { nombre: linkedList.length })}
                        </Typography.Text>
                    </Divider>
                    {linkedList.length === 0 && (
                        <div style={{
                            padding: '12px 14px', background: '#f8fafc', borderRadius: 8,
                            textAlign: 'center', color: '#9ca3af', fontSize: 12, marginBottom: 8,
                        }}>
                            {t('reglements.detail.aucunRapproche')}
                        </div>
                    )}
                    {linkedList.map(e => (
                        <div key={e.encaissementTrackingId} style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600, fontSize: 13 }}>{e.numeroCheque}</span>
                                <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 12 }}>{formatDate(e.dateEncaissement)}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Tag color={e.statutCheque === 'ENCAISSE' ? 'green' : 'default'} style={{ borderRadius: 12, margin: 0 }}>{e.statutCheque}</Tag>
                                <span style={{ fontWeight: 700, color: '#1a6b3c' }}>{formatMontant(e.montantCheque)}</span>
                                {canComptable && (
                                    <Tooltip title={t('reglements.detail.delier')}>
                                        <Button
                                            size="small"
                                            type="text"
                                            danger
                                            icon={<DisconnectOutlined />}
                                            loading={delierLoadingId === e.encaissementTrackingId}
                                            onClick={() => handleDelier(e.encaissementTrackingId)}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    ))}
                    {canRapprocher && canComptable && (
                        <div style={{ marginTop: 8, textAlign: 'right' }}>
                            <Button
                                size="small"
                                icon={<LinkOutlined />}
                                onClick={() => setPickerOpen(true)}
                                style={{ borderRadius: 8 }}
                            >
                                {t('reglements.detail.rapprocher')}
                            </Button>
                        </div>
                    )}
                </>

                <Divider style={{ margin: '16px 0 12px' }} />
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    <Trans t={t} i18nKey="reglements.detail.creeLe" values={{ date: formatDate(paiement.createdAt), auteur: paiement.createdBy }} components={{ b: <strong /> }} />
                    {paiement.updatedAt && <> <Trans t={t} i18nKey="reglements.detail.modifieLe" values={{ date: formatDate(paiement.updatedAt), auteur: paiement.updatedBy }} components={{ b: <strong /> }} /></>}
                </div>
            </Drawer>
            <RapprochementPickerModal
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                paiement={paiement}
                onConfirmed={onRefresh}
            />
        </>
    );
};

//  Banner d'état encaissement vs règlement
const EncaissementStatusBanner = ({ status, loading }) => {
    const { t } = useTranslation('dossierFinance');
    if (loading || !status) return null;
    const { regleAOk, regleBOk, regleCOk, totalEncaisse, totalEngage, couverture } = status;

    const totalPrefi = status?.totalPrefinancementsActifs ?? 0;
    const prefinancementInfo = totalPrefi > 0 ? (
        <Alert
            type="info"
            showIcon
            message={<span><Trans t={t} i18nKey="reglements.bandeau.prefinancementActif" values={{ montant: formatMontant(totalPrefi) }} components={{ b: <strong /> }} /></span>}
            description={(status?.totalEncaissementsActifs ?? 0) === 0
                ? t('reglements.bandeau.autoriseParPrefinancement')
                : t('reglements.bandeau.prefinancementCouvre')}
            style={{ marginBottom: 12, borderRadius: 10 }}
        />
    ) : null;

    if (!regleAOk) {
        return (
            <>
                {prefinancementInfo}
                <Alert
                    style={{ marginBottom: 16, borderRadius: 10 }}
                    type="error"
                    showIcon
                    message={t('reglements.bandeau.aucunEncaissement')}
                    action={
                        <Button size="small" onClick={() => window.dispatchEvent(new CustomEvent('cb:goto-tab', { detail: 'encaissements' }))}>
                            {t('reglements.bandeau.saisirEncaissement')}
                        </Button>
                    }
                />
            </>
        );
    }
    if (!regleBOk) {
        return (
            <>
                {prefinancementInfo}
                <Alert
                    style={{ marginBottom: 16, borderRadius: 10 }}
                    type="warning"
                    showIcon
                    message={t('reglements.bandeau.aucunChequeCredite')}
                />
            </>
        );
    }
    if (!regleCOk) {
        return (
            <>
                {prefinancementInfo}
                <Alert
                    style={{ marginBottom: 16, borderRadius: 10 }}
                    type="warning"
                    showIcon
                    message={t('reglements.bandeau.couvertureInsuffisante', { encaisse: formatMontant(totalEncaisse), engage: formatMontant(totalEngage) })}
                />
            </>
        );
    }
    return (
        <>
            {prefinancementInfo}
            {/* <Alert
                style={{ marginBottom: 16, borderRadius: 10 }}
                type="success"
                showIcon
                message={
                    <span>
                        Encaissé <strong>{formatMontant(totalEncaisse)}</strong> ·
                        Engagé <strong>{formatMontant(totalEngage)}</strong> ·
                        Disponible <strong>{formatMontant(couverture)}</strong>
                    </span>
                }
            /> */}
        </>
    );
};

// COMPOSANT PRINCIPAL
/* LARGEURS PARTAGEES PAR L'EN-TETE ET LES BRANCHES.
   Une seule source : declarees deux fois, l'en-tete et les lignes auraient
   derive a la premiere colonne retouchee, et des titres decales au-dessus de
   leurs valeurs se lisent plus mal que pas de titres du tout.

   `GOUTTIERE` est la largeur reservee aux connecteurs de l'arborescence :
   l'en-tete doit s'en decaler d'autant pour tomber sur les colonnes. */
const OP_GOUTTIERE = 26;

/* DECALAGE D'UN NIVEAU D'ARBORESCENCE.
   Il s'ajoute a la gouttiere, donc au DEBUT de la ligne. Les colonnes de
   droite - cheque, montant, date, statut - restent collees au bord droit et
   ne bougent pas d'un niveau a l'autre : c'est la colonne beneficiaire, la
   seule elastique, qui absorbe le decalage. Sans cela, indenter une branche
   aurait desaligne tous les montants sous leur en-tete. */
const OP_INDENT = 22;

/* LES MEMES COLONNES, ET AUX MEMES LARGEURS, QUE LE TABLEAU PARENT.
   Une operation rattachee est un reglement : elle se decrit par les memes
   informations. Des colonnes differentes obligeaient a relire un second jeu
   d'en-tetes pour comprendre ce qu'on avait sous les yeux, et empechaient de
   comparer une ligne a sa racine d'un coup d'oeil.

   Les largeurs sont celles des colonnes correspondantes du tableau : ce qui
   permet aux branches de tomber sous elles au lieu de former une grille
   parallele. */
const OP_COLS = {
    reference: 170,
    type: 130,
    /* COLONNE PROPRE AUX BRANCHES. Le tableau parent n'a pas de colonne pour le
       cheque : seules les operations comptables en portent un, et l'ajouter a la
       liste des reglements l'aurait laissee vide sur la plupart des lignes.
       C'est l'unique ecart assume avec les colonnes de la racine. */
    cheque: 130,
    montant: 150,
    date: 120,
    statut: 140,
    action: 32,
};

/** Ligne de titres du sous-tableau, alignee sur les branches. */
function EnteteOperations() {
    const { t } = useTranslation('dossierFinance');
    const cell = (largeur, texte, align = 'center') => (
        <span style={{
            width: largeur, flexShrink: 0, textAlign: align,
            fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em',
            textTransform: 'uppercase', color: '#6b7280',
        }}>
            {texte}
        </span>
    );

    /* FOND GRIS SUR TOUTE LA LARGEUR, gouttiere comprise : une ligne de titres
       qui s'arreterait au bord des connecteurs se lirait comme une branche
       teintee, pas comme un en-tete de tableau. */
    return (
        <div style={{
            display: 'flex', alignItems: 'center',
            background: '#f1f3f5', borderRadius: 6,
            borderBottom: '1px solid #d9e2dc',
        }}>
            <span style={{ width: OP_GOUTTIERE, flexShrink: 0 }} />
            <div style={{
                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10,
                padding: '5px 10px',
            }}>
                {cell(OP_COLS.reference, t('reglements.colonnes.numeroReglement'))}
                {cell(OP_COLS.type, t('reglements.colonnes.type'))}
                <span style={{
                    flex: 1, minWidth: 0, fontSize: 10.5, fontWeight: 700,
                    letterSpacing: '.03em', textTransform: 'uppercase', color: '#6b7280',
                }}>
                    {t('reglements.colonnes.beneficiaire')}
                </span>
                {cell(OP_COLS.cheque, t('reglements.colonnes.numeroCheque'))}
                {cell(OP_COLS.montant, t('reglements.colonnes.montant'), 'right')}
                {cell(OP_COLS.date, t('reglements.colonnes.dateEmission'))}
                {cell(OP_COLS.statut, t('reglements.colonnes.statut'))}
                <span style={{ width: OP_COLS.action, flexShrink: 0 }} />
            </div>
        </div>
    );
}

/**
 * Une operation rattachee a un reglement, rendue comme une branche.
 *
 * Porte exactement ce que le sous-tableau montrait : type, reference, detail,
 * montant, date, statut et l'acces au detail. Les largeurs sont fixes pour que
 * les colonnes restent alignees d'une branche a l'autre sous une meme racine.
 */
function BrancheOperation({ operation: c, parent, derniere, niveau = 1, traits = [], onOuvrir }) {
    const { t } = useTranslation('dossierFinance');
    const annule = c.typeOperation === 'ANNULATION_REGLEMENT';

    // Abscisse du filet vertical de CETTE ligne. Chaque niveau decale de OP_INDENT.
    const filet = (rang) => 9 + rang * OP_INDENT;
    const mien = filet(niveau - 1);

    return (
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
            {/* Connecteurs. La derniere branche coupe la verticale a mi-hauteur :
                sinon le trait descendrait dans le vide sous la derniere ligne.

                Les filets des NIVEAUX AU-DESSUS traversent la ligne de part en
                part quand leur fratrie n'est pas finie : sans eux, une operation
                imbriquee couperait visuellement l'arbre en deux. */}
            <div style={{
                width: OP_GOUTTIERE + (niveau - 1) * OP_INDENT,
                position: 'relative', flexShrink: 0,
            }}>
                {traits.map((continu, rang) => continu && (
                    <span key={rang} style={{
                        position: 'absolute', left: filet(rang), top: 0, bottom: 0,
                        borderLeft: '1px solid #c9ddd1',
                    }} />
                ))}
                <span style={{
                    position: 'absolute', left: mien, top: 0,
                    bottom: derniere ? '50%' : 0,
                    borderLeft: '1px solid #c9ddd1',
                }} />
                <span style={{
                    position: 'absolute', left: mien, top: '50%', width: 12,
                    borderTop: '1px solid #c9ddd1',
                }} />
            </div>

            <div style={{
                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 10px', borderRadius: 8,
                background: annule ? '#fef2f2' : 'transparent',
            }}>
                {/* Le numero d'abord : c'est par lui qu'on identifie l'operation,
                    le type ne fait que la qualifier. L'en-tete suit le meme ordre. */}
                <Tag color="geekblue"
                    style={{ fontFamily: 'var(--font-main)', fontWeight: 600, fontSize: 12, margin: 0, flexShrink: 0, width: OP_COLS.reference, textAlign: 'center' }}>
                    {c.reference || c.numeroPaiement || c.paiementTrackingId?.slice(0, 8) || '-'}
                </Tag>

                <Tag color={TYPE_OPERATION_COLORS[c.typeOperation] || 'default'}
                    style={{ borderRadius: 12, fontWeight: 600, fontSize: 11, margin: 0, flexShrink: 0, width: OP_COLS.type, textAlign: 'center' }}>
                    {TYPE_OPERATION_LABELS[c.typeOperation] || t('reglements.operation')}
                </Tag>

                {/* BENEFICIAIRE, comme dans le tableau parent. Le detail qui
                    occupait cette place - motif d'annulation, numero de cheque -
                    n'est pas perdu : il passe en seconde ligne, sous le nom. Le
                    supprimer aurait coute une information qu'aucune autre colonne
                    ne porte. */}
                <span style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                    <span style={{
                        display: 'block', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {c.beneficiaire || <span style={{ color: '#9ca3af' }}>-</span>}
                    </span>
                    {/* Le motif d'annulation reste en seconde ligne : aucune
                        colonne ne le porte, et le perdre couterait la raison meme
                        de l'annulation. Le numero de cheque, lui, a desormais la
                        sienne. */}
                    {annule && (
                        <span style={{ display: 'block', fontSize: 11, color: '#991b1b', fontStyle: 'italic' }}>
                            {t('reglements.annuleReference', { reference: parent?.reference || t('reglements.ceReglement') })}
                            {c.motifAnnulation && <> - {c.motifAnnulation}</>}
                        </span>
                    )}
                </span>

                <span style={{
                    width: OP_COLS.cheque, textAlign: 'center', flexShrink: 0,
                    fontSize: 12, fontFamily: 'var(--font-main)',
                    color: c.numeroChequeEmis ? '#065f46' : '#9ca3af',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {c.numeroChequeEmis || '-'}
                </span>

                <span style={{
                    width: OP_COLS.montant, textAlign: 'right', flexShrink: 0, fontWeight: 700,
                    color: annule ? '#dc2626' : '#1a6b3c', fontFamily: 'var(--font-main)',
                }}>
                    {/* Meme traitement que la colonne Montant du tableau parent :
                        le signe est sorti du nombre formate, pour que « - 150 000 »
                        se lise d'un coup d'oeil au lieu d'un « -150 000 » colle. */}
                    {c.montant < 0 ? '- ' : ''}{formatMontant(Math.abs(c.montant || 0))}
                </span>

                <span style={{ width: OP_COLS.date, textAlign: 'center', flexShrink: 0, fontSize: 12, color: '#6b7280' }}>
                    {c.createdAt ? new Date(c.createdAt).toLocaleDateString(localeIntl()) : '-'}
                </span>

                <span style={{ width: OP_COLS.statut, textAlign: 'center', flexShrink: 0 }}>
                    <StatutTag statut={c.statut} />
                </span>

                {/* Largeur imposee, identique a celle que l'en-tete reserve :
                    un bouton laisse a sa taille naturelle decalerait la colonne
                    de statut d'une branche a l'autre. */}
                <span style={{ width: OP_COLS.action, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title={t('reglements.voirDetails')}>
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            style={{ borderRadius: 6 }}
                            onClick={() => onOuvrir(c)}
                        />
                    </Tooltip>
                </span>
            </div>
        </div>
    );
}

export default function OngletReglements({ sinistre, victimes, prefill = null, onPrefillConsomme }) {
    const { t } = useTranslation('dossierFinance');
    /* TROIS ETATS POUR UNE SEULE REPONSE.
       `reglements` est l'arbre tel que le serveur l'envoie, et c'est lui que le
       tableau rend. `paiements` en est la mise a plat, pour la recherche,
       l'export et les compteurs de statuts qui n'ont que faire de la
       profondeur - derivee de l'arbre plutot que demandee a part, les deux ne
       peuvent pas diverger. `totaux` porte les montants, calcules par le
       serveur : ils l'etaient ici, et l'un d'eux etait faux. */
    const [reglements, setReglements] = useState([]);
    const [paiements, setPaiements] = useState([]);
    const [totaux, setTotaux] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const [showCreer, setShowCreer] = useState(false);
    // Arrivée depuis une offre acceptée : la fenêtre s'ouvre déjà remplie.
    useEffect(() => { if (prefill) setShowCreer(true); }, [prefill]);
    const [showDetail, setShowDetail] = useState(false);
    const [showAnnuler, setShowAnnuler] = useState(false);
    const [showValiderTech, setShowValiderTech] = useState(false);
    const [showReglComptable, setShowReglComptable] = useState(false);
    const [showValiderComptable, setShowValiderComptable] = useState(false);
    const [paiementAction, setPaiementAction] = useState(null);
    const [filteredPaiements, setFilteredPaiements] = useState([]);
    const [search, setSearch] = useState('');

    const [statusRefreshKey, setStatusRefreshKey] = useState(0);
    const [encStatus, setEncStatus] = useState(null);
    const [encStatusLoading, setEncStatusLoading] = useState(false);

    useEffect(() => {
        if (!sinistre?.sinistreTrackingId) return;
        setEncStatusLoading(true);
        axiosInstance.get(`/v1/prefinancements/couverture/${sinistre.sinistreTrackingId}`)
            .then(res => {
                const c = res.data?.data;
                setEncStatus({
                    regleAOk: c?.regleAOk ?? false,
                    regleBOk: c?.regleBOk ?? false,
                    regleCOk: c?.regleCOk ?? false,
                    // Le backend nomme ce champ `message` (CouvertureSinistreResponse) :
                    // le lire sous un autre nom faisait retomber tous les blocages sur le
                    // texte générique « aucun encaissement déclaré », même quand la vraie
                    // raison était un chèque non crédité ou une couverture insuffisante.
                    messageBlocant: c?.message ?? c?.messageBlocant,
                    totalEncaisse: c?.totalEncaisseActif,
                    totalEngage: c?.totalPaiementsActifs,
                    couverture: c?.soldeNet,
                    totalEncaissementsActifs: c?.totalEncaisseActif,
                    totalPrefinancementsActifs: c?.totalPrefiActif,
                    // `encaissements` est la liste des cheques retenus pour
                    // couvrir ce sinistre : son cardinal est le nombre affiche
                    // sous le total encaisse.
                    nbEncaissements: c?.encaissements?.length ?? 0,
                });
            })
            .catch(() => { /* silent */ })
            .finally(() => setEncStatusLoading(false));
    }, [sinistre?.sinistreTrackingId, statusRefreshKey]);

    const canRedacteur = true;
    const canComptable = true;

    const load = useCallback(() => {
        if (!sinistre?.sinistreTrackingId) return;
        setLoading(true);
        axiosInstance.get(`/v1/paiements/sinistre/${sinistre.sinistreTrackingId}`)
            .then(r => {
                const arbre = r.data?.data?.reglements || [];
                const plat = aplatirEnListe(arbre);
                setReglements(arbre);
                setPaiements(plat);
                setFilteredPaiements(plat);
                setTotaux(r.data?.data?.totaux || null);
            })
            .catch(() => toast.error(i18n.t('dossierFinance:reglements.erreurs.reglements')))
            .finally(() => {
                setLoading(false);
                setStatusRefreshKey(k => k + 1);
            });
    }, [sinistre?.sinistreTrackingId]);

    useEffect(() => { load(); }, [load]);

    /* L'ARBRE ARRIVE CONSTRUIT. Cette memo reconstruisait le rattachement a
       partir des seuls `parentCodeId`, en remontant chaque chaine jusqu'a sa
       racine. Elle le reconstruisait a PLAT : toutes les operations d'un
       reglement ressortaient au meme niveau, si bien que l'annulation d'un
       reglement comptable se lisait comme la soeur de ce comptable alors
       qu'elle en depend. Le serveur etablit desormais ce rattachement, ou il
       est a sa place et ou il est teste.

       `childrenByParent` garde son sens d'avant - TOUS les descendants d'une
       racine, a plat - parce que c'est ce dont la recherche et le marqueur
       d'annulation ont besoin. Le tableau, lui, lit l'arbre directement. */
    const { mainPaiements, childrenByParent } = useMemo(() => {
        const childMap = {};
        for (const racine of reglements) {
            const descendants = aplatirEnListe(racine.operations);
            if (descendants.length) {
                childMap[String(racine.paiementTrackingId)] = descendants;
            }
        }
        return { mainPaiements: reglements, childrenByParent: childMap };
    }, [reglements]);

    const expandableRowKeys = useMemo(() => Object.keys(childrenByParent), [childrenByParent]);

    /* DEPLIE PAR DEFAUT. Les operations rattachees a un reglement sont
       precisement ce qu'on vient consulter : exiger un clic par ligne pour les
       voir revenait a cacher l'information principale.

       L'ETAT EST INDEXE SUR L'IDENTIFIANT, JAMAIS SUR LE RANG. Le tri et les
       filtres du tableau reordonnent les lignes : un etat indexe sur la position
       aurait suivi la place plutot que le reglement, et replie la mauvaise ligne
       apres un tri.

       Recalcule quand la liste change, pour qu'un reglement nouvellement cree
       arrive deplie comme les autres. Ce que l'utilisateur a REPLIE entre-temps
       est perdu a ce moment-la : c'est le prix d'un defaut deplie, et il se paie
       rarement - la liste ne change qu'apres une action de l'utilisateur. */
    const [lignesDepliees, setLignesDepliees] = useState([]);
    useEffect(() => {
        setLignesDepliees(expandableRowKeys);
    }, [expandableRowKeys]);

    const searchedPaiements = useMemo(() => {
        if (!search) return mainPaiements;
        const q = search.toLowerCase();
        const matchOne = (p) => (
            (p.numeroPaiement || '').toLowerCase().includes(q)
            || (p.beneficiaire || '').toLowerCase().includes(q)
            || (p.numeroChequeEmis || '').toLowerCase().includes(q)
            || (p.motifAnnulation || '').toLowerCase().includes(q)
        );
        return mainPaiements.filter(rt => {
            if (matchOne(rt)) return true;
            const enfants = childrenByParent[String(rt.paiementTrackingId)] || [];
            return enfants.some(matchOne);
        });
    }, [mainPaiements, childrenByParent, search]);

    const getChildren = (record) => childrenByParent[String(record?.paiementTrackingId)];

    /**
     * Les operations rattachees a un reglement, en arborescence.
     *
     * POURQUOI PAS UN TABLEAU IMBRIQUE. Un `Table` dans un `Table` reintroduisait
     * ses propres en-tetes de colonnes sous chaque ligne depliee : on relisait
     * « Type / Reference / Detail / Montant » a chaque reglement, alors que le
     * tableau parent les portait deja. L'arborescence montre le rattachement
     * lui-meme, qui est l'information qu'on vient chercher.
     *
     * LES CONNECTEURS SONT DES BORDURES, comme sur les sinistres lies : un filet
     * vertical qui descend de la racine, un raccord horizontal vers la ligne. La
     * derniere branche coupe la verticale a mi-hauteur, sinon le trait
     * continuerait dans le vide sous la derniere operation.
     */
    const renderChildrenExpanded = (record) => {
        const children = getChildren(record);
        if (!children?.length) return null;

        /* LE RATTACHEMENT VIENT DU SERVEUR, le DESSIN vient d'ici. `operations`
           porte l'arbre tel qu'il est en base ; `aplatirOperations` n'en deduit
           que la mise en page - profondeur, derniere branche, filets a
           prolonger. L'ecran lisait ce rattachement a plat, et l'annulation
           d'un reglement comptable s'affichait a cote de lui au lieu d'en
           dependre. */
        const branches = aplatirOperations(record.operations);

        return (
            <div style={{ padding: '4px 0 8px 48px', background: '#fff' }}>
                <EnteteOperations />
                {branches.map((b) => (
                    <BrancheOperation
                        key={b.operation.paiementTrackingId}
                        operation={b.operation}
                        // Au premier niveau, le parent est la racine elle-meme :
                        // elle n'est pas dans la liste des descendants.
                        parent={b.parent || record}
                        derniere={b.derniere}
                        niveau={b.niveau}
                        traits={b.traits}
                        onOuvrir={ouvrirDetail}
                    />
                ))}
            </div>
        );
    };

    const ouvrirDetail = async (row) => {
        try {
            const r = await axiosInstance.get(`/v1/paiements/${row.paiementTrackingId}`);
            setSelected(r.data?.data);
            setShowDetail(true);
        } catch {
            toast.error(t('reglements.erreurs.detail'));
        }
    };

    const lancerAnnulation = (p) => { setPaiementAction(p); setShowDetail(false); setShowAnnuler(true); };
    const lancerValiderTechnique = (p) => { setPaiementAction(p); setShowDetail(false); setShowValiderTech(true); };
    const lancerReglementComptable = (p) => { setPaiementAction(p); setShowDetail(false); setShowReglComptable(true); };
    const lancerValiderComptable = (p) => { setPaiementAction(p); setShowDetail(false); setShowValiderComptable(true); };

    const fetchPrintPayload = async (p) => {
        const [detailRes, sinistreRes] = await Promise.all([
            axiosInstance.get(`/v1/paiements/${p.paiementTrackingId}`),
            axiosInstance.get(`/v1/sinistres/${sinistre.sinistreTrackingId}`),
        ]);
        const detail = detailRes.data?.data;
        const sinistreFull = sinistreRes.data?.data;
        // SinistreResponse n'expose que l'ID - fetch séparé pour logo/headerImageUrl/signataires.
        const organismeId = sinistreFull?.organismeMembreTrackingId;
        let organisme = null;
        if (organismeId) {
            try {
                const orgRes = await axiosInstance.get(`/v1/organismes/${organismeId}`);
                organisme = orgRes.data?.data || null;
            } catch {
                organisme = null;
            }
        }
        /* En-tête des documents : le bureau Carte Brune (seul organisme BUREAU_NATIONAL),
           et non l'assureur du dossier - la quittance sortait au nom d'une compagnie. */
        let bureau = null;
        try {
            const res = await axiosInstance.get('/v1/organismes/type/BUREAU_NATIONAL', { skipErrorToast: true });
            bureau = (res.data?.data || [])[0] || null;
        } catch { bureau = null; }
        return { detail, sinistreFull, organisme, bureau };
    };

    // Vérifie qu'une URL d'image charge - fallback logo local au render si KO.
    const resolveOrganismeForPrint = async (organisme) => {
        if (!organisme) return organisme;
        const baseUrl = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '');
        const candidate = organisme.headerImageUrl || organisme.logo;
        const fullUrl = candidate && !/^(https?:|data:)/i.test(candidate) ? `${baseUrl}${candidate}` : candidate;
        if (!fullUrl) return organisme;
        const ok = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = fullUrl;
        });
        return ok ? organisme : { ...organisme, headerImageUrl: null, logo: null };
    };

    // On avertit avant le tirage : les signatures expirent, et le document sortirait
    // vierge sans que personne s'en apercoive avant de regarder la feuille.
    const lancerPrintOrdre = (p) => confirmerImpressionSansSignature('ORDRE_DEPENSE', async () => {
        try {
            const { detail, sinistreFull, organisme, bureau } = await fetchPrintPayload(p);
            const orgPrint = await resolveOrganismeForPrint(organisme);
            const bureauPrint = await resolveOrganismeForPrint(bureau);
            /* Chargee AVANT le rendu : renderToStaticMarkup serialise le resultat en
               chaine, et aucun effet React ne s'y execute - BlocSignature ne chargeait
               donc jamais rien, et ce document sortait sans signature. */
            const signature = await chargerSignatureImprimable(
                'ORDRE_DEPENSE', sinistreFull?.sinistreTrackingId);
            const html = renderToStaticMarkup(<OrdreDeDepenseV2 reglement={detail} sinistre={sinistreFull} organisme={orgPrint} bureau={bureauPrint} signature={signature} />);
            /* LA FEUILLE DOIT SUIVRE LE DOCUMENT. `renderToStaticMarkup` produit du
               HTML nu, imprime dans un document SEPARE : l'`import` de la feuille
               dans le composant alimente l'apercu a l'ecran, jamais ce document-la.
               L'ordre de depense n'en avait pas besoin tant qu'il portait ses styles
               en ligne ; depuis qu'il partage les classes de la quittance, l'omettre
               le faisait sortir sans en-tete ni tableaux. */
            printInIframe(html, quittanceCss, 'Ordre de dépense');
        } catch {
            toast.error(t('reglements.erreurs.ordreDepense'));
        }
    });

    const lancerPrintQuittance = (p) => confirmerImpressionSansSignature('QUITTANCE_INDEMNITE', async () => {
        try {
            const { detail, sinistreFull, organisme, bureau } = await fetchPrintPayload(p);
            const orgPrint = await resolveOrganismeForPrint(organisme);
            const bureauPrint = await resolveOrganismeForPrint(bureau);
            const signature = await chargerSignatureImprimable(
                'QUITTANCE_INDEMNITE', sinistreFull?.sinistreTrackingId);

            /* LA VERIFICATION SE FAIT ICI, APRES LE CHARGEMENT. Ces champs
               viennent du dossier complet, que seul `fetchPrintPayload` ramene :
               les controler avant l'appel aurait porte sur la ligne de liste,
               qui ne les contient pas. */
            const adversaire = sinistreFull?.adversaires?.[0] ?? sinistreFull?.adversaire ?? null;
            const assureNom = [adversaire?.personneNom, adversaire?.personnePrenom]
                .filter(Boolean).join(' ');

            await confirmerChampsManquants(
                [
                    { libelle: t('reglements.champsManquants.numeroAssureur'), valeur: sinistreFull?.numeroSinistreAssureur },
                    { libelle: t('reglements.champsManquants.assure'), valeur: assureNom },
                ],
                () => {
                    /* Bénéficiaire victime anglophone (fiche) : quittance bilingue. Organisme,
                       expert ou victime francophone : la quittance d'avant. */
                    const beneficiaire = detail?.beneficiaireDetail;
                    const langue = beneficiaire?.type === 'VICTIME'
                        ? langueVictime((victimes || []).find(v => v.victimeTrackingId === beneficiaire.trackingId))
                        : 'fr';
                    const html = renderToStaticMarkup(<QuittanceIndemnite decaissement={detail} sinistre={sinistreFull} organisme={orgPrint} bureau={bureauPrint} signature={signature} langue={langue} />);
                    printInIframe(html, quittanceCss, "Quittance d'indemnité");
                },
            );
        } catch {
            toast.error(t('reglements.erreurs.quittance'));
        }
    });

    const afterAction = () => {
        load();
        setSelected(null);
        setStatusRefreshKey(k => k + 1);
    };

    const refreshSelectedPaiement = useCallback(async () => {
        load();
        setStatusRefreshKey(k => k + 1);
        if (selected?.paiementTrackingId) {
            try {
                const r = await axiosInstance.get(`/v1/paiements/${selected.paiementTrackingId}`);
                setSelected(r.data?.data);
            } catch {
                /* silencieux */
            }
        }
    }, [selected?.paiementTrackingId, load]);

    const hasAnnulationForCurrent = useMemo(() => {
        const current = selected;
        if (!current?.paiementTrackingId) return false;
        const trackingId = String(current.paiementTrackingId);
        return paiements.some(p =>
            p.typeOperation === 'ANNULATION_REGLEMENT'
            && String(p.parentCodeId) === trackingId
        );
    }, [selected, paiements]);

    /**
     * Le reglement ouvert a-t-il donne lieu a un reglement comptable VALIDE ?
     *
     * Si oui, il ne s'annule plus : la validation comptable est le moment ou
     * l'argent sort. On regarde le STATUT `PAYE` des lignes filles, et non leur
     * simple existence - un comptable saisi mais pas encore valide, lui, reste
     * annulable, et doit meme l'etre avant son technique.
     */
    const comptableValidePourCurrent = useMemo(() => {
        const trackingId = selected?.paiementTrackingId;
        if (!trackingId) return false;
        return paiements.some(p =>
            p.statut === 'PAYE' && String(p.parentCodeId) === String(trackingId),
        );
    }, [selected, paiements]);

    const hasActiveRcForCurrent = useMemo(() => {
        const current = selected;
        if (!current) return false;
        const trackingId = current.paiementTrackingId;
        if (!trackingId) return false;
        const rcs = paiements.filter(p =>
            p.typeOperation === 'REGLEMENT_COMPTABLE'
            && p.parentCodeId === String(trackingId)
        );
        const annulationsOfRcs = paiements.filter(p =>
            p.typeOperation === 'ANNULATION_REGLEMENT'
            && rcs.some(rc => String(rc.paiementTrackingId) === String(p.parentCodeId))
        );
        const annulledRcIds = new Set(annulationsOfRcs.map(a => String(a.parentCodeId)));
        return rcs.some(rc =>
            rc.statut !== 'ANNULE'
            && !annulledRcIds.has(String(rc.paiementTrackingId))
        );
    }, [selected, paiements]);

    const kpis = useMemo(() => {
        /* LES MONTANTS VIENNENT DU SERVEUR. Ils etaient calcules ici, a partir
           de la liste des lignes, et la regle existait donc en DOUBLE - en SQL
           la-bas, en JavaScript ici. Les deux ont diverge : l'ecran doublait le
           total engage a chaque validation comptable, parce que le reglement
           comptable RECOPIE le montant du technique et que la somme prenait les
           deux lignes. Le nombre restait plausible, simplement deux fois trop
           grand, ce qui est la pire forme d'erreur sur un montant.

           `engage` sort desormais de la requete qui autorise ou bloque un
           reglement : l'ecran ne peut plus contredire la regle qui l'arbitre.

           LES COMPTEURS DE STATUTS, EUX, RESTENT ICI. Ils n'arbitrent rien, ils
           decrivent la liste affichee, et la liste est deja sous la main. */
        return {
            /* `engage` n'est plus lu nulle part : aucune carte ne l'affiche
               depuis que les deux dernieres portent des nombres. Le serveur
               l'envoie quand meme - il fonde `resteADecaisser` - et le
               reafficher ne couterait qu'une ligne. */
            totalDecaisse: Number(totaux?.decaisse || 0),
            resteADecaisser: Number(totaux?.resteADecaisser || 0),
            nbTechnique: Number(totaux?.nombreTechnique || 0),
            nbComptable: Number(totaux?.nombreComptable || 0),
            nbEmis: paiements.filter(p => p.statut === 'EMIS').length,
            nbTechValide: paiements.filter(p => p.statut === 'REGLEMENT_TECHNIQUE_VALIDE').length,
            nbComptValide: paiements.filter(p => p.statut === 'REGLEMENT_COMPTABLE_VALIDE').length,
            nbPaye: paiements.filter(p => p.statut === 'PAYE').length,
            nbAnnule: paiements.filter(p => p.statut === 'ANNULE').length,
        };
    }, [paiements, totaux]);

    const organismeInfo = sinistre?.contrat?.organisme || sinistre?.organisme;

    const sinistreNumeroAffichable = sinistre?.numeroSinistreLocal
        || sinistre?.numeroSinistreManuel
        || sinistre?.sinistreReference
        || sinistre?.sinistreTrackingId?.slice(0, 8)
        || '';

    const exportColumns = [
        { title: t('reglements.export.niveau'), dataIndex: 'niveau' },
        { title: t('reglements.colonnes.numeroReglement'), dataIndex: 'numeroOperation' },
        { title: t('reglements.export.lieART'), dataIndex: 'lieARt' },
        { title: t('reglements.colonnes.beneficiaire'), dataIndex: 'beneficiaire' },
        { title: t('reglements.export.typePrejudice'), dataIndex: 'typePrejudice' },
        { title: t('reglements.colonnes.montant'), dataIndex: 'montant', format: 'currency', numeric: true },
        { title: t('reglements.colonnes.statut'), dataIndex: 'statut' },
        { title: t('reglements.colonnes.numeroCheque'), dataIndex: 'numeroCheque' },
        { title: t('reglements.colonnes.banque'), dataIndex: 'banque' },
        { title: t('reglements.colonnes.dateEmission'), dataIndex: 'dateEmission' },
        { title: t('reglements.export.datePaiement'), dataIndex: 'datePaiement' },
        { title: t('reglements.export.motifAnnulation'), dataIndex: 'motifAnnulation' },
    ];

    const toExportRow = (p, niveau, parentRt) => {
        const isChild = niveau === 'RC' || niveau === 'AN';
        const niveauLabel = niveau === 'RT' ? t('reglements.export.niveaux.technique')
            : niveau === 'RC' ? t('reglements.export.niveaux.comptable')
                : niveau === 'AN' ? t('reglements.export.niveaux.annulation') : '';
        return {
            niveau: niveauLabel,
            numeroOperation: (isChild ? '> ' : '') + (p.numeroPaiement || p.reference || p.paiementTrackingId?.slice(0, 12) || ''),
            lieARt: isChild && parentRt ? (parentRt.numeroPaiement || String(parentRt.paiementTrackingId).slice(0, 12)) : '',
            beneficiaire: p.beneficiaire || (parentRt?.beneficiaire || ''),
            typePrejudice: p.typePrejudice || (parentRt?.typePrejudice || ''),
            montant: p.montant != null ? Number(p.montant) : (parentRt?.montant != null ? Number(parentRt.montant) : ''),
            statut: STATUT_CONFIG[p.statut]?.label || p.statut || '',
            numeroCheque: p.numeroChequeEmis || '',
            banque: p.banqueCheque || '',
            dateEmission: p.dateEmission ? new Date(p.dateEmission).toLocaleDateString(localeIntl()) : '',
            datePaiement: p.datePaiement ? new Date(p.datePaiement).toLocaleDateString(localeIntl()) : '',
            motifAnnulation: p.motifAnnulation || '',
        };
    };

    const exportDidParseCell = (data) => {
        if (data.section !== 'body') return;
        const niveau = String(data.row.raw[0] || '');
        if (niveau === t('reglements.export.niveaux.comptable')) {
            data.cell.styles.fillColor = [236, 253, 245];
        } else if (niveau === t('reglements.export.niveaux.annulation')) {
            data.cell.styles.fillColor = [254, 242, 242];
            data.cell.styles.textColor = [127, 29, 29];
        } else if (niveau === t('reglements.export.niveaux.technique')) {
            data.cell.styles.fontStyle = 'bold';
        }
    };

    const detectChildNiveau = (child) => {
        if (child.typeOperation === 'ANNULATION_REGLEMENT' || child.statut === 'ANNULE') return 'AN';
        if (child.typeOperation === 'REGLEMENT_COMPTABLE'
            || child.statut === 'REGLEMENT_COMPTABLE_VALIDE'
            || child.statut === 'PAYE') return 'RC';
        return 'RC';
    };

    const promoteBankInfoToRt = (rt, enfants) => {
        if (rt.banqueCheque && rt.numeroChequeEmis) return rt;
        const rcActif = enfants.find(c =>
            (c.typeOperation === 'REGLEMENT_COMPTABLE' || c.statut === 'REGLEMENT_COMPTABLE_VALIDE' || c.statut === 'PAYE')
            && c.banqueCheque
        );
        if (!rcActif) return rt;
        return {
            ...rt,
            banqueCheque: rt.banqueCheque || rcActif.banqueCheque,
            numeroChequeEmis: rt.numeroChequeEmis || rcActif.numeroChequeEmis,
            datePaiement: rt.datePaiement || rcActif.datePaiement,
        };
    };

    const buildExportRows = () => {
        const baseList = filteredPaiements.length ? filteredPaiements : mainPaiements;
        const rows = [];
        for (const rt of baseList) {
            const enfants = childrenByParent[String(rt.paiementTrackingId)] || [];
            const rtEnrichi = promoteBankInfoToRt(rt, enfants);
            rows.push(toExportRow(rtEnrichi, 'RT'));
            for (const c of enfants) {
                rows.push(toExportRow(c, detectChildNiveau(c), rtEnrichi));
            }
        }
        return rows;
    };

    const rowHasAnnulation = useCallback((row) => {
        if (!row?.paiementTrackingId) return false;
        const trackingId = String(row.paiementTrackingId);
        return paiements.some(p =>
            p.typeOperation === 'ANNULATION_REGLEMENT'
            && String(p.parentCodeId) === trackingId
        );
    }, [paiements]);

    // Vrai s'il existe un RC enfant non-annulé pour cette ligne RT.
    const rowHasActiveRc = useCallback((row) => {
        if (!row?.paiementTrackingId) return false;
        const trackingId = String(row.paiementTrackingId);
        const rcs = paiements.filter(p =>
            p.typeOperation === 'REGLEMENT_COMPTABLE'
            && String(p.parentCodeId) === trackingId
        );
        if (rcs.length === 0) return false;
        const annulationsOfRcs = paiements.filter(p =>
            p.typeOperation === 'ANNULATION_REGLEMENT'
            && rcs.some(rc => String(rc.paiementTrackingId) === String(p.parentCodeId))
        );
        const annulledRcIds = new Set(annulationsOfRcs.map(a => String(a.parentCodeId)));
        return rcs.some(rc =>
            rc.statut !== 'ANNULE'
            && !annulledRcIds.has(String(rc.paiementTrackingId))
        );
    }, [paiements]);

    const cols = [
        {
            title: t('reglements.colonnes.numeroReglement'),
            dataIndex: 'numeroPaiement',
            key: 'numeroPaiement',
            render: (value, row) => value ? (
                <Tag color={TYPE_OPERATION_COLORS[row.typeOperation] || 'default'}>
                    {value}
                </Tag>
            ) : null,
            sorter: (a, b) => (a.numeroPaiement || '').localeCompare(b.numeroPaiement || ''),
        },
        {
            title: '-',
            dataIndex: 'reference',
            ellipsis: true,
            render: (ref, record) => (
                <div>
                    {ref ? (
                        <Tag color="geekblue" style={{ fontFamily: 'var(--font-main)', fontWeight: 600, fontSize: 12, padding: '2px 8px' }}>
                            {ref}
                        </Tag>
                    ) : (
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>-</span>
                    )}
                    {(childrenByParent[String(record.paiementTrackingId)] || []).some(c => c.typeOperation === 'ANNULATION_REGLEMENT' || c.statut === 'ANNULE') && (
                        <Tag color="red" style={{ fontSize: 10, padding: '0 6px', marginLeft: 6, fontWeight: 600, borderStyle: 'dashed' }}>
                            {t('reglements.annuleMajuscules')}
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: t('reglements.colonnes.type'),
            dataIndex: 'typeOperation',
            width: 130,
            render: (type) => type ? (
                <Tag color={TYPE_OPERATION_COLORS[type]}>
                    {TYPE_OPERATION_LABELS[type] || type}
                </Tag>
            ) : <Tag color="blue">{t('reglements.reglement')}</Tag>,
            filters: Object.entries(TYPE_OPERATION_LABELS).map(([k, v]) => ({ text: v, value: k })),
            onFilter: (value, record) => record.typeOperation === value,
        },
        {
            title: t('reglements.colonnes.beneficiaire'),
            dataIndex: 'beneficiaire',
            ellipsis: true,
        },
        // {
        // title: 'Organisme',
        // key: 'organisme',
        // ellipsis: true,
        // render: (_, record) => {
        // const org = record.organisme || organismeInfo;
        // return org ? (
        // <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        // <BankOutlined style={{ color: '#2563eb', fontSize: 12 }} />
        // <span style={{ fontSize: 12 }}>{org.raisonSociale || org.nom || '-'}</span>
        // </div>
        // ) : <span style={{ color: '#9ca3af' }}>-</span>;
        // },
        // },
        {
            title: t('reglements.colonnes.montant'),
            dataIndex: 'montant',
            width: 150,
            align: 'right',
            render: v => (
                <span style={{ fontWeight: 700, color: v < 0 ? '#dc2626' : '#1a6b3c', fontFamily: 'var(--font-main)' }}>
                    {v < 0 ? '- ' : ''}{formatMontant(Math.abs(v || 0))}
                </span>
            ),
            sorter: (a, b) => a.montant - b.montant,
        },
        {
            title: t('reglements.colonnes.dateEmission'),
            dataIndex: 'dateEmission',
            width: 120,
            render: v => v ? new Date(v).toLocaleDateString(localeIntl()) : '-',
            sorter: (a, b) => (a.dateEmission || '').localeCompare(b.dateEmission || ''),
        },
        {
            title: t('reglements.colonnes.statut'),
            dataIndex: 'statut',
            width: 140,
            render: v => <StatutTag statut={v} />,
            filters: Object.entries(STATUT_CONFIG).map(([k, v]) => ({ text: v.label, value: k })),
            onFilter: (value, record) => record.statut === value,
        },
        {
            title: t('reglements.colonnes.actions'),
            width: 220,
            align: 'right',
            render: (_, row) => {
                const s = row.statut;
                return (
                    <Space size={4} wrap>
                        <Tooltip title={t('reglements.voirLeDetail')}>
                            <Button icon={<EyeOutlined />} size="small" style={{ borderRadius: 6 }}
                                onClick={() => ouvrirDetail(row)} />
                        </Tooltip>
                        {s === 'EMIS' && canRedacteur && (
                            <Tooltip title={t('reglements.validerTechnique.titre')}>
                                <Button icon={<CheckCircleOutlined />} size="small"
                                    style={{ borderRadius: 6, color: '#2563eb', borderColor: '#2563eb' }}
                                    onClick={() => lancerValiderTechnique(row)} />
                            </Tooltip>
                        )}
                        {s === 'REGLEMENT_TECHNIQUE_VALIDE' && canComptable && (
                            <>
                                <Tooltip title={t('reglements.imprimerOrdre')}>
                                    <Button icon={<PrinterOutlined />} size="small"
                                        style={{ borderRadius: 6, color: '#7c3aed', borderColor: '#7c3aed' }}
                                        onClick={() => lancerPrintOrdre(row)} />
                                </Tooltip>
                                {(() => {
                                    const rcDejaSaisi = rowHasActiveRc(row);
                                    const rcTooltip = rcDejaSaisi
                                        ? t('reglements.rcDejaSaisiAide')
                                        : (!encStatus?.regleBOk ? (encStatus?.messageBlocant || t('reglements.aucunChequeCredite')) : t('reglements.saisirComptable'));
                                    return (
                                        <Tooltip title={rcTooltip}>
                                            <Button icon={<FileDoneOutlined />} size="small"
                                                disabled={rcDejaSaisi || encStatusLoading || !encStatus?.regleBOk}
                                                style={{ borderRadius: 6, color: '#059669', borderColor: '#059669' }}
                                                onClick={() => lancerReglementComptable(row)} />
                                        </Tooltip>
                                    );
                                })()}
                            </>
                        )}
                        {s === 'REGLEMENT_COMPTABLE_VALIDE' && canComptable && (
                            <Tooltip title={!encStatus?.regleCOk ? (encStatus?.messageBlocant || t('reglements.couvertureInsuffisante')) : t('reglements.validerComptable.titre')}>
                                <Button icon={<CheckCircleOutlined />} size="small"
                                    disabled={encStatusLoading || !encStatus?.regleCOk}
                                    style={{ borderRadius: 6, color: '#059669', borderColor: '#059669' }}
                                    onClick={() => lancerValiderComptable(row)} />
                            </Tooltip>
                        )}
                        {s === 'PAYE' && (
                            <Tooltip title={t('reglements.imprimerLaQuittance')}>
                                <Button icon={<FileTextOutlined />} size="small"
                                    style={{ borderRadius: 6, color: '#6b7280', borderColor: '#6b7280' }}
                                    onClick={() => lancerPrintQuittance(row)} />
                            </Tooltip>
                        )}
                        {s !== 'ANNULE' && s !== 'PAYE' && (canRedacteur || canComptable) && !rowHasAnnulation(row) && (
                            <Tooltip title={t('reglements.annulerCeReglement')}>
                                <Button icon={<StopOutlined />} size="small" danger
                                    style={{ borderRadius: 6 }}
                                    onClick={() => lancerAnnulation(row)} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <div>
                {organismeInfo && (
                    <div style={{
                        background: 'linear-gradient(90deg, #eff6ff 0%, #f0fdf4 100%)',
                        border: '1px solid #bfdbfe',
                        borderRadius: 10, padding: '10px 18px', marginBottom: 16,
                        display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                        <BankOutlined style={{ color: '#2563eb', fontSize: 20 }} />
                        <div>
                            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>{t('reglements.detail.organisme')}</div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>
                                {organismeInfo.raisonSociale || organismeInfo.nom}
                                {organismeInfo.code && (
                                    <Tag style={{ marginLeft: 8, borderRadius: 12, fontFamily: 'var(--font-main)', fontSize: 11 }}>
                                        {organismeInfo.code}
                                    </Tag>
                                )}
                            </div>
                            {organismeInfo.typeOrganisme && (
                                <div style={{ fontSize: 11, color: '#6b7280' }}>{organismeInfo.typeOrganisme}</div>
                            )}
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: 20 }}>
                    <Typography.Title level={4} style={{ margin: 0 }}>{t('reglements.titre')}</Typography.Title>
                    <Space>
                        {canRedacteur && (
                            <Tooltip title={
                                !isDossierMurOuPlus(sinistre?.statut)
                                    ? t('reglements.dossierNonMur', { statut: STATUT_MAP[sinistre?.statut]?.label || sinistre?.statut || '-' })
                                    : !encStatus?.regleAOk ? (encStatus?.messageBlocant || t('reglements.aucunEncaissementDeclare')) : ''
                            }>
                                <Button type="primary" icon={<PlusOutlined />}
                                    disabled={!isDossierMurOuPlus(sinistre?.statut) || encStatusLoading || !encStatus?.regleAOk}
                                    style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
                                    onClick={() => setShowCreer(true)}>
                                    {t('reglements.nouveau')}
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title={t('reglements.actualiser')}>
                            <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 8 }} />
                        </Tooltip>
                    </Space>
                </div>

                <EncaissementStatusBanner status={encStatus} loading={encStatusLoading} />

                {/* LES QUATRE CARTES SUIVENT LE PARCOURS DE L'ARGENT : ce qui
                    entre, ce qui sort, puis les deux etapes par lesquelles il
                    sort.

                    LES DEUX PREMIERES PORTENT DES MONTANTS, LES DEUX DERNIERES
                    DES NOMBRES. Un montant technique et un montant comptable
                    disent la meme somme a deux etapes de son parcours : affiches
                    cote a cote, ils se lisaient comme deux depenses distinctes.
                    Le compte, lui, repond a la seule question que ces deux cartes
                    ont a trancher - combien de reglements en sont ou.

                    Le compteur « Annules », qu'une carte portait seule, a rejoint
                    la frise des statuts juste en dessous : il y est a sa place,
                    parmi les autres etats, et n'occupe plus une carte entiere. */}
                <KpiCards
                    style={{ marginTop: 12 }}
                    columns={4}
                    items={[
                        {
                            label: t('reglements.kpi.totalEncaissement'),
                            value: formatMontant(encStatus?.totalEncaissementsActifs ?? 0),
                            color: '#2563eb', bg: '#eff6ff',
                            suffix: t('reglements.kpi.encaissements', { count: encStatus?.nbEncaissements ?? 0 }),
                        },
                        {
                            label: t('reglements.kpi.totalDecaisse'),
                            value: formatMontant(kpis.totalDecaisse),
                            color: '#059669', bg: '#ecfdf5',
                            // Ce qui est engage mais pas encore sorti : la carte
                            // dit ce qui est parti, le sous-titre ce qui doit partir.
                            suffix: t('reglements.kpi.resteADecaisser', { montant: formatMontant(kpis.resteADecaisser) }),
                        },
                        {
                            label: t('reglements.kpi.technique'),
                            value: kpis.nbTechnique,
                            color: '#1a6b3c', bg: '#f0fdf4',
                            // « actif » dit ce que le compte EXCLUT : les
                            // reglements annules, qui restent des lignes en base.
                            suffix: t('reglements.kpi.actifs', { count: kpis.nbTechnique }),
                        },
                        {
                            label: t('reglements.kpi.comptable'),
                            value: kpis.nbComptable,
                            color: '#0891b2', bg: '#ecfeff',
                            suffix: t('reglements.kpi.actifs', { count: kpis.nbComptable }),
                        },
                        ...((encStatus?.totalPrefinancementsActifs ?? 0) > 0
                            ? [{ label: t('reglements.kpi.prefinance'), value: formatMontant(encStatus.totalPrefinancementsActifs), color: '#a16207', bg: '#fefce8', suffix: t('reglements.kpi.aRembourser') }]
                            : []),
                    ]}
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '10px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                    {[
                        { label: t('reglements.statuts.EMIS'), count: kpis.nbEmis, color: '#94a3b8' },
                        { arrow: true },
                        { label: t('reglements.statuts.REGLEMENT_TECHNIQUE_VALIDE'), count: kpis.nbTechValide, color: '#2563eb' },
                        { arrow: true },
                        { label: t('reglements.statuts.REGLEMENT_COMPTABLE_VALIDE'), count: kpis.nbComptValide, color: '#0891b2' },
                        { arrow: true },
                        { label: t('reglements.statuts.PAYE'), count: kpis.nbPaye, color: '#059669' },
                        /* Hors sequence, et volontairement : une annulation ne
                           suit pas l'etape precedente, elle la defait. Le
                           separateur est donc une barre, pas une fleche. */
                        { separateur: true },
                        { label: t('reglements.annules'), count: kpis.nbAnnule, color: '#dc2626' },
                    ].map((item, i) => item.arrow ? (
                        <ArrowRightOutlined key={i} style={{ color: '#cbd5e1', fontSize: 12 }} />
                    ) : item.separateur ? (
                        <span key={i} style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0', margin: '0 4px' }} />
                    ) : (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Badge count={item.count} style={{ backgroundColor: item.color }} showZero>
                                <Tag style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, color: item.color, borderColor: item.color + '44', background: item.color + '11', padding: '2px 10px' }}>
                                    {item.label}
                                </Tag>
                            </Badge>
                        </div>
                    ))}
                </div>

                <div style={{ marginBottom: 16 }}>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder={t('reglements.recherche')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        allowClear
                    />
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>
                ) : (
                    <Table
                        className="cb-table"
                        dataSource={searchedPaiements}
                        columns={cols}
                        rowKey="paiementTrackingId"
                        size="small"
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        rowClassName={record => {
                            const classes = [];
                            if (record.statut === 'ANNULE') classes.push('cb-row-annule');
                            const isExpandable = expandableRowKeys.includes(String(record.paiementTrackingId));
                            classes.push(isExpandable ? 'cb-row-rt-expanded' : 'cb-row-rt');
                            return classes.join(' ');
                        }}
                        onChange={(_, filters, sorter, extra) => {
                            setFilteredPaiements(extra.currentDataSource || searchedPaiements);
                        }}
                        expandable={{
                            expandedRowRender: renderChildrenExpanded,
                            rowExpandable: record => expandableRowKeys.includes(String(record.paiementTrackingId)),
                            expandedRowKeys: lignesDepliees,
                            onExpandedRowsChange: (cles) => setLignesDepliees(cles.map(String)),
                            indentSize: 0,
                            expandIcon: ({ expanded, onExpand, record }) => {
                                if (!expandableRowKeys.includes(String(record.paiementTrackingId))) return null;
                                return (
                                    <RightOutlined
                                        rotate={expanded ? 90 : 0}
                                        onClick={(e) => onExpand(record, e)}
                                        style={{
                                            color: '#1a6b3c',
                                            transition: 'transform 0.2s',
                                            cursor: 'pointer',
                                            marginRight: 8,
                                        }}
                                    />
                                );
                            },
                        }}
                        locale={{
                            emptyText: (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <div>
                                            <div style={{ marginBottom: 8 }}>{t('reglements.aucunReglement')}</div>
                                            {canRedacteur && encStatus?.regleAOk && (
                                                <Button type="primary" size="small" icon={<PlusOutlined />}
                                                    style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
                                                    onClick={() => setShowCreer(true)}>
                                                    {t('reglements.creerPremier')}
                                                </Button>
                                            )}
                                            {canRedacteur && !encStatus?.regleAOk && (
                                                <div style={{ color: '#9ca3af', fontSize: 12 }}>
                                                    {t('reglements.encaissementDabord')}
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            ),
                        }}
                    />
                )}

                <TableExportButton
                    floating
                    dataLoader={async () => buildExportRows()}
                    columns={exportColumns}
                    filename={`reglements_sinistre_${sinistreNumeroAffichable.replace(/[^A-Za-z0-9]/g, '_') || 'export'}`}
                    title={t('reglements.export.titre', { numero: sinistreNumeroAffichable })}
                    subtitle={organismeInfo?.raisonSociale || organismeInfo?.nom ? t('reglements.export.organisme', { organisme: organismeInfo.raisonSociale || organismeInfo.nom }) : ''}
                    count={filteredPaiements.length || mainPaiements.length}
                    logo={logoLocal}
                    didParseCell={exportDidParseCell}
                />

                {/* Modals & Drawer */}
                <ModalReglementTechnique
                    open={showCreer}
                    prefill={prefill}
                    onClose={() => { setShowCreer(false); onPrefillConsomme?.(); }}
                    sinistre={sinistre}
                    victimes={victimes}
                    onCreated={load}
                    encStatus={encStatus}
                />

                <DetailDrawer
                    paiement={selected}
                    open={showDetail}
                    onClose={() => { setShowDetail(false); setSelected(null); }}
                    onAnnuler={lancerAnnulation}
                    onValiderTechnique={lancerValiderTechnique}
                    onReglementComptable={lancerReglementComptable}
                    onValiderComptable={lancerValiderComptable}
                    onPrintOrdre={lancerPrintOrdre}
                    onPrintQuittance={lancerPrintQuittance}
                    canComptable={canComptable}
                    canRedacteur={canRedacteur}
                    encStatus={encStatus}
                    encStatusLoading={encStatusLoading}
                    onRefresh={refreshSelectedPaiement}
                    hasActiveRc={hasActiveRcForCurrent}
                    comptableValide={comptableValidePourCurrent}
                    hasAnnulation={hasAnnulationForCurrent}
                />

                <ModalValiderTechnique
                    paiement={paiementAction}
                    open={showValiderTech}
                    onClose={() => { setShowValiderTech(false); setPaiementAction(null); }}
                    onValide={afterAction}
                />

                <ModalReglementComptable
                    paiement={paiementAction}
                    open={showReglComptable}
                    onClose={() => { setShowReglComptable(false); setPaiementAction(null); }}
                    onCreated={afterAction}
                    encStatus={encStatus}
                />

                <ModalValiderComptable
                    paiement={paiementAction}
                    open={showValiderComptable}
                    onClose={() => { setShowValiderComptable(false); setPaiementAction(null); }}
                    onValide={afterAction}
                    encStatus={encStatus}
                />

                <ModalAnnuler
                    paiement={paiementAction}
                    open={showAnnuler}
                    onClose={() => { setShowAnnuler(false); setPaiementAction(null); }}
                    onAnnule={afterAction}
                />
            </div>

            {/*  Section règlements par lot d'experts  */}
            {sinistre?.sinistreTrackingId && (
                <ReglementsByLotSection sinistreTrackingId={sinistre.sinistreTrackingId} />
            )}
        </>
    );
}

//  Section règlements par lot d'experts 
const STATUT_LOT_DISPLAY_ONGLET = avecLibelleTraduit({
    EMIS: { color: 'default', icon: <ClockCircleOutlined /> },
    REGLEMENT_TECHNIQUE_VALIDE: { color: 'processing', icon: <FileDoneOutlined /> },
    REGLEMENT_COMPTABLE_VALIDE: { color: 'cyan', icon: <BankOutlined /> },
    PAYE: { color: 'success', icon: <CheckCircleOutlined /> },
    PARTIELLEMENT_ANNULE: { color: 'warning', icon: <CloseCircleOutlined /> },
    ANNULE: { color: 'error', icon: <CloseCircleOutlined /> },
}, 'dossierFinance:reglements.statutsLot');

function ReglementsByLotSection({ sinistreTrackingId }) {
    const { t } = useTranslation('dossierFinance');
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!sinistreTrackingId) return;
        setLoading(true);
        // Tente l'endpoint dédié, repli silencieux si absent
        axiosInstance
            .get(`/v1/lot-reglements/by-sinistre/${sinistreTrackingId}`)
            .then((r) => {
                const data = r.data?.data ?? r.data ?? [];
                setLots(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                // Endpoint non implémenté ou aucun résultat : on ne bloque pas
                setLots([]);
            })
            .finally(() => setLoading(false));
    }, [sinistreTrackingId]);

    if (!loading && lots.length === 0) return null;

    const cols = [
        {
            title: t('reglements.lots.idLot'),
            dataIndex: 'lotTrackingId',
            key: 'lotTrackingId',
            width: 110,
            render: (v) => (
                <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c' }}>
                    {v?.slice(0, 8).toUpperCase()}
                </span>
            ),
        },
        {
            title: t('reglements.lots.expert'),
            dataIndex: 'expertNomComplet',
            key: 'expert',
            width: 200,
            ellipsis: true,
            render: (v) => v ?? '-',
        },
        {
            title: t('reglements.lots.numeroLot'),
            dataIndex: 'numeroLot',
            key: 'numeroLot',
            width: 160,
            render: (v) => v
                ? <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600, color: '#1a6b3c' }}>{v}</span>
                : '-',
        },
        {
            title: t('reglements.lots.statutLot'),
            dataIndex: 'statut',
            key: 'statut',
            width: 160,
            render: (v) => {
                const d = STATUT_LOT_DISPLAY_ONGLET[v] ?? { color: 'default', label: v ?? '-', icon: null };
                return (
                    <Tag color={d.color} icon={d.icon} style={{ borderRadius: 20, fontWeight: 600 }}>
                        {d.label}
                    </Tag>
                );
            },
        },
        {
            title: '',
            key: 'action',
            width: 80,
            render: (_, record) =>
                record.lotTrackingId ? (
                    <Tooltip title={t('reglements.lots.ouvrirAide')}>
                        <Button
                            type="link"
                            icon={<ExportOutlined />}
                            style={{ color: '#1a6b3c', padding: 0, fontSize: 13 }}
                            onClick={() => window.open(`/lot-reglements/${record.lotTrackingId}`, '_blank')}
                        >
                            {t('reglements.lots.allerVersLot')}
                        </Button>
                    </Tooltip>
                ) : null,
        },
    ];

    return (
        <div style={{ marginTop: 32 }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    paddingBottom: 10,
                    borderBottom: '1px solid #e5e7eb',
                }}
            >
                <TeamOutlined style={{ color: '#1a6b3c', fontSize: 18 }} />
                <Typography.Title level={5} style={{ margin: 0, color: '#374151' }}>
                    {t('reglements.lots.titre')}
                </Typography.Title>
                {lots.length > 0 && (
                    <Tag color="green" style={{ borderRadius: 12, marginLeft: 4 }}>
                        {lots.length}
                    </Tag>
                )}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin size="small" />
                </div>
            ) : (
                <Table
                    style={{
                        marginBottom: "120px"
                    }}
                    className="cb-table"
                    dataSource={lots}
                    columns={cols}
                    rowKey={(r) => r.lotTrackingId ?? r.numeroLot ?? Math.random()}
                    size="small"
                    pagination={false}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span style={{ color: '#6b7280', fontSize: 13 }}>
                                        {t('reglements.lots.aucun')}
                                    </span>
                                }
                            />
                        ),
                    }}
                    scroll={{ x: 'max-content' }}
                />
            )}
        </div>
    );
}